import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/store';
import { Button, Field, Icon, Modal, PageTree, Text, type PageTreeNode } from '../../../../components';
import { FtuxTarget } from '../../../../features/ftux/FtuxTarget';
import { ftuxSignal, useDragDemoOn, useGamesShown, useListsShown } from '../../../../features/ftux/signals';
import type { FtuxTargetId } from '../../../../features/ftux/targets';
import { useMediaQuery } from '@/nova/hooks/useMediaQuery';
import { MOBILE_MQ, SORTABLE_ANIM } from '@/nova/theme/tokens';
import { scrollParentOf, useSortableRows } from '../../../blog-editor/hooks/useSortableRows';
import { previewFrameSrc, type DeviceMode, type DsiSourceInfo } from '../../live-preview';
import { useSiteBridge } from '../../useSiteBridge';
import { useSiteTranslations } from '../../useSiteTranslations';
import { creativesServiceConfigured } from '../../creative-api';
import {
  getTenantSourceStateShared,
  publishTenantSource,
  type TenantSourceFailure,
} from '../../creatives-tenant-source';
import { afterPublish, liveTotal, snapshotFromState, type OrderedSourceSnapshot } from '../../ordered-source';
import { publishOrderFor, unpublishedLists } from '../../unpublished-lists';
import { useOrderedSource } from '../../hooks/useOrderedSource';
import {
  listLabel,
  listTitles,
  sectionListIds,
  sectionSourceId,
  serviceListIds,
  serviceListTitles,
} from '../../section-titles';
import {
  HOME_PAGE,
  asSourceContent,
  gameKey,
  gameListsFromSources,
  gamesOf,
  isOfferedGameList,
  isWithheldPlatformList,
  mergeShownOrder,
  orderedGames,
  pinnedContent,
  providerNamesFromContent,
  providerSourceId,
  orderHolds,
  sameOrder,
  sourceTotal,
  type GamePage,
  type SiteGame,
  type SiteSourceContent,
} from '../../site-games';
import {
  deleteOwnerGameList,
  getOwnerGameLists,
  isSiteGameListId,
  ownListsInPreview,
  saveOwnerGameList,
  siteGameLists,
  type ListNames,
  type OwnListKind,
  type OwnListMode,
} from '../../owner-game-lists-api';
import { localizeListName, nameLanguagesOf } from '../../list-name-api';
import { useSiteLanguages } from '../../hooks/useSiteLanguages';
import type { ListFilters } from '../../game-filters';
import { GAME_CATALOG_SOURCE_ID, gamesOfSource, type GameSourceRecord } from '../../game-sources';
import { cachedGameSource, fetchGameSource } from '../../games-api';
import { ownListChanged, ownListGames, ownListMembers, type OwnListState } from '../../own-lists';
import { useGameSources } from '../../useGameSources';
import { getMarketCollectionsShared } from '../../market-collections-api';
import { getSitePagesShared, pageArrangeable, peekSitePages, type SitePage } from '../../site-pages-api';
import { siteText } from '../../site-translations';
import { pageListsVerdict, pageStructure, type SitePagesRead } from '../../page-lists-verdict';
import { gamesDeepLink } from '../../games-deep-link';
import {
  INSPIRATION_SITES,
  initialOrderVerdict,
  showsMexicanLists,
  type InitialOrderVerdict,
} from '../../initial-order';
import { readInitialOrderNoteClosed, writeInitialOrderNoteClosed } from '../../initial-order-note';
import {
  labelIn,
  inNavOrder,
  linkedPaths,
  listNavLabel,
  navLinks,
  navigationLabels,
  navigationSourceIds,
  pathLabel,
  titleCaseShouting,
  type NavLink,
} from '../../page-labels';
import { groupOf, groupSitePages, offeredSitePages } from '../../site-page-tabs';
import { usePageArrangement } from '../../hooks/usePageArrangement';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { arrangementWithOwnLists, OWN_LISTS_FLAG, ownListsState } from '../../own-lists-gate';
import {
  listChanged,
  listMoveFromOrder,
  pageListRows,
  resolveArrangement,
  sameArrangement,
  withListMoved,
  withListRows,
  type ListMove,
  type PageListRow,
  type PageOwnList,
} from '../../page-lists';
import type { ReorderRow } from '../../list-reorder';
import { useIsAlive } from '../../../configurator-v3-mx/lib/liveness';
import { SwitchFade } from '../../creatives.styles';
import {
  GAME_LISTS_COLUMNS_MIN_PX,
  GAME_LISTS_GAP_PX,
  GAME_LISTS_LABELLED_ACTIONS_MIN_PX,
  GAME_LISTS_PANES_MIN_PX,
  HELD_HEAD_VAR,
  SCREEN_CHROME,
  scrollAreaOf,
} from '../../game-list-geometry';
import { useWebsiteHeaderAction, useWebsiteHeaderOverride } from '../../../website/header-action';
import { useAboveAppChrome } from '../../../../transitions';
import { CustomListEditor, type CustomListDraft } from '../CustomListEditor';
import { Wrap, EmptyState, EmptyTitle, EmptyText } from '../CarouselCuration/CarouselCuration.styles';
import { GameListGrid } from '../GameListGrid';
import { GameListsSkeleton } from '../GameListsSkeleton';
import { GameListPreview } from '../GameListPreview';
import { type GameListSheetPublish } from '../GameListSheet';
import { ListActions } from '../ListActions';
import { ListHeading } from '../ListHeading';
import { DeleteBtn as DeleteListBtn } from '../CustomListEditor/CustomListEditor.styles';
import { ListRow } from '../ListRow';
import { PreviewOverlay } from '../PreviewOverlay';
import { DevicePreviewPill } from '../DevicePreviewPill';
import { ListReorderModal } from '../ListReorderModal';
import {
  ProbeFrame,
  AddListBtn,
  Notes,
  Note,
  NoteText,
  RetryBtn,
  RowWrap,
  ScreenReaderText,
  ChangeBar,
  Body,
  GamesHead,
  GamesPane,
  GamesScroll,
  HEAD_FADE_PX,
  ListsColumn,
  ListsHead,
  ListsTitles,
  ListsTitle,
  ListsMeta,
  ListRows,
  ListRowsScroll,
  ListsFooter,
  ListsFooterSurface,
  ListsTop,
  PagesLevel,
  LevelBack,
  LevelActions,
  InfoPointBox,
  InfoPointBtn,
  InfoPointFold,
  InfoPointGlyph,
  InfoPointNote,
  InfoPointText,
  PublishAllBtn,
  PublishLabel,
  PUBLISH_CHECK_HOLD_MS,
  PUBLISH_FADE_MS,
  PUBLISH_OPEN_MS,
  Columns,
  TreeColumn,
  MainColumn,
} from './GameLists.styles';

export type GameListsProps = {
  /** The brand's deployed site base URL (the preview host). Empty while unknown. */
  siteUrl: string;
  /** True while the site host is still being resolved (shows loading instead of "no website"). */
  resolving?: boolean;
  /** The active brand's casino instance: what the game-list service is keyed by. Absent → no publishing. */
  casinoInstanceId?: string;
  /** Shows a short confirmation or failure (the page's toast). */
  onNotify?: (message: string) => void;
};

/** Stop polling the page's sources once its game lists, the provider names and its sections have loaded: the
 *  section source the service names (`named`), when it names one, and not just any page source the site reports.
 *  With a name, the lists that count are the ones its sections place (`placed`: as the site serves them once they are
 *  read, else as they are named before that, by the service when the chips are drawn on its word, or under a list
 *  page's list by the arrangement of the page there; null while neither is known): the page may report another page's
 *  lists before its own, which come after its sections. Only a list the tab offers counts (one still loading, empty or
 *  withheld does not). Sections that place no list count any, as without a name. Every list in `awaited` counts too:
 *  the lists so named, which the site reports one at a time (one can come well after the others) and whose games are
 *  read from it only once it has. The page tree's navigation decision does not wait for those (see `navSourcesIn`). */
const pageSourcesLoaded =
  (named: string, placed: string[] | null, awaited: readonly string[]) =>
  (sources: DsiSourceInfo[]): boolean => {
    if (named && placed === null) return false;
    const lists = gameListsFromSources(sources);
    const listsIn = placed?.length ? lists.some((list) => placed.includes(list.id)) : lists.length > 0;
    return (
      listsIn &&
      awaited.every((id) => lists.some((list) => list.id === id)) &&
      providerSourceId(sources) !== null &&
      sectionSourceId(sources, named) !== null
    );
  };

/** How long a page's chips wait for its lists' states before they are drawn without them (see `statesIn`). */
const LIST_STATES_WAIT_MS = 4_000;

/** How long the page tree waits for the pages the tab offers to be decided before it is drawn without it (see
 *  `pageTabsSettled`): the decision needs the site's navigation, the service's word on it or, when the service cannot
 *  say, the navigation read from the framed site. */
const PAGES_DECIDE_WAIT_MS = 10_000;

/** How long, once the site could not be reached, the tab keeps loading for the chips the service may still draw (see
 *  `failureHeld`) before it says so. */
const SERVICE_HOLD_MS = 10_000;

/** How long the page tree, and the chips drawn on the service's word, wait for the site's translation table before they
 *  name the site's pages and lists as the sources give them (see `siteWordsIn`). */
const SITE_WORDS_WAIT_MS = 2_000;

/** The host of the brand's site URL, what the service reads the site's pages by; '' while there is none, or for one
 *  that is not a URL. */
const hostOf = (url: string): string => {
  try {
    return url ? new URL(url).host : '';
  } catch {
    return '';
  }
};

/** Which read of the site's pages an end is for: the brand, the site's host and the attempt (Try again bumps it). */
const sitePagesKeyOf = (casinoInstanceId: string | undefined, host: string, attempt: number): string =>
  `${casinoInstanceId ?? ''}|${host}|${attempt}`;

/** The info point on one visit of a page: whether its note is open (null until decided), whether that is still the
 *  decision the rule took rather than the owner's click, and the lists counted for it so far. */
type InfoPointState = { page: GamePage; open: boolean | null; byRule: boolean; counted: ReadonlySet<string> };

const enteredInfoPoint = (page: GamePage): InfoPointState => ({ page, open: null, byRule: true, counted: new Set() });

/** An owner's own game list, built in the Add List popup: a name and the games it holds, in order.
 *
 *  It holds game IDS, not game objects, because ids are what the service stores. The tiles are resolved
 *  against the catalogue when the list is opened, so a list survives a reload without the tab having to
 *  carry every game object for every list. `sourceId` is the data source the site renders it from, which is
 *  how the page names it, and how the site reports it once the page shows it. `gameIds` is the stored DRAFT;
 *  `publishedIds` is what the site shows, and the two differ while the owner has unpublished changes. `kind`
 *  and `layout` are the list's shape, which reaches the site with the page's arrangement, as its name does.
 *  `mode` and `filters` say how the list is built (see `own-lists`): for an automatic list `gameIds` is only
 *  the owner's order over whatever its filters match. Both have a draft and a published side, as the games do. */
type CustomList = {
  id: string;
  name: string;
  /** The name in each website language (see `OwnerGameList.names`); null when it has none. */
  names: ListNames | null;
  gameIds: string[];
  sourceId: string;
  publishedIds: string[];
  kind: OwnListKind;
  layout: string;
  mode: OwnListMode;
  filters: ListFilters | null;
  publishedMode: OwnListMode;
  publishedFilters: ListFilters | null;
};

/** The owner's lists by the page each is stored under. */
type ListsByPage = Partial<Record<GamePage, CustomList[]>>;

/** A page with none of the owner's lists: one value, so a page's lists keep their identity while its arrangement changes. */
const NO_OWN_LISTS: CustomList[] = [];

/** One of the owner's lists by its id, under whichever page it is stored: a list page shows the lists stored under
 *  another route of its list as well (see `pageCustom`), so a write to one of them finds it where it is. */
const ownListById = (byPage: ListsByPage, id: string): CustomList | undefined => {
  for (const lists of Object.values(byPage)) {
    const found = lists?.find((list) => list.id === id);
    if (found) return found;
  }
  return undefined;
};

/** The owner's lists with the one of `id` changed by `change`, under whichever page it is stored. */
const withOwnListChanged = (byPage: ListsByPage, id: string, change: (list: CustomList) => CustomList): ListsByPage => {
  const next: ListsByPage = {};
  for (const [path, lists] of Object.entries(byPage))
    next[path] = lists?.map((list) => (list.id === id ? change(list) : list));
  return next;
};

/** The owner's lists without the one of `id`, under whichever page it was stored. */
const withoutOwnList = (byPage: ListsByPage, id: string): ListsByPage => {
  const next: ListsByPage = {};
  for (const [path, lists] of Object.entries(byPage)) next[path] = lists?.filter((list) => list.id !== id);
  return next;
};

/** A list as the owner is editing it, with its order given (a reorder still being stored, say). */
const workingStateOf = (custom: CustomList, order: readonly string[]): OwnListState => ({
  mode: custom.mode,
  filters: custom.filters,
  order,
});

/** A list as the site shows it. */
const publishedStateOf = (custom: CustomList): OwnListState => ({
  mode: custom.publishedMode,
  filters: custom.publishedFilters,
  order: custom.publishedIds,
});

/** What the page's Publish changes shows: whether it shows, and how many changes it publishes. Held as one
 *  value so a drag can freeze all of it together. */
type ChangeBarState = { show: boolean; sendable: number };

/**
 * Creatives → Games: reorder the games in one of the site's game lists, preview the new order on the live
 * site, and publish it (SPB-327). The lists are read from the deployed site itself over the dsi bridge (a
 * transparent probe frame of the chosen page), so what the tab shows as the list's order is what the site
 * renders. Publishing writes the owner's order as the brand's tenant data source through spark-creatives;
 * where that service or its game-list routes are not available, Publish stays off and says so.
 */

/** A row of the column of lists: a place the page shows a list (`row`), or a list it does not place (`row` null). A list
 *  the page shows more than once has a row for each place, numbered (`appearance`). */
type ColumnRow = { key: string; list: { id: string; count?: number }; row: PageListRow | null; appearance?: number };

/** The FTUX's publish target (named for the list's own Publish it lit before Publish changes was the one publish). */
const PUBLISH_TARGET: FtuxTargetId = 'games-publish-list';

/** Holds the page over the app's chrome while it is drawn (`useAboveAppChrome`): the lists' footer sits over the phone's
 *  bottom navigation, under its scrim otherwise (the user, 2026-10-21). */
const AboveAppChrome = ({ when }: { when: boolean }): null => {
  useAboveAppChrome(when);
  return null;
};

const NO_GAMES: readonly GameSourceRecord[] = [];

/**
 * The FULL game catalogue (art and all) while `enabled`: undefined while it is read, null when there is none to hand
 * (off, no brand yet, or a read that failed or brought no games). The tab holds the SLIM catalogue (`useGameSources`);
 * this one is the live preview's (see `ownListsInPreview`). Kept once read (per brand, in `games-api`); a read that
 * failed is tried again the next time `enabled` turns on.
 */
const useFullCatalogue = (enabled: boolean): readonly GameSourceRecord[] | null | undefined => {
  // The raw value, coerced outside the selector (see useGameSources: a NaN selector result re-renders on every dispatch).
  const brandTenant = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const tenantRaw = Number(brandTenant);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const on = enabled && tenantId !== undefined;
  // The brand whose read failed while on. Turned off, that is forgotten: the next time on reads again.
  const [failedFor, setFailedFor] = React.useState<number | null>(null);
  if (!on && failedFor !== null) setFailedFor(null);
  // Bumped when the read lands, to render again: what is handed out is read from the cache.
  const [, setLanded] = React.useState(0);
  const renderedHeld = on && tenantId !== undefined && cachedGameSource(tenantId, GAME_CATALOG_SOURCE_ID) !== undefined;
  React.useEffect(() => {
    if (!on || tenantId === undefined) return undefined;
    if (cachedGameSource(tenantId, GAME_CATALOG_SOURCE_ID)) {
      // A read that landed between this render and this effect (another caller's): render again to hand it out, or the
      // preview waits on it until something else re-renders the tab.
      if (!renderedHeld) setLanded((n) => n + 1);
      return undefined;
    }
    let stale = false;
    fetchGameSource(tenantId, GAME_CATALOG_SOURCE_ID).then(
      (blob) => {
        if (stale) return;
        // A 200 with no source is not kept by the cache: it counts as a failed read, which settles the wait.
        if (gamesOfSource(blob).length === 0) setFailedFor(tenantId);
        else setLanded((n) => n + 1);
      },
      () => {
        if (!stale) setFailedFor(tenantId);
      },
    );
    return () => {
      stale = true;
    };
  }, [on, tenantId, renderedHeld]);
  if (!on || tenantId === undefined) return null;
  const cached = cachedGameSource(tenantId, GAME_CATALOG_SOURCE_ID);
  if (cached) {
    const games = gamesOfSource(cached);
    return games.length > 0 ? games : null;
  }
  return failedFor === tenantId ? null : undefined;
};

export const GameLists = ({
  siteUrl,
  resolving = false,
  casinoInstanceId,
  onNotify,
}: GameListsProps): React.ReactElement => {
  const { t, i18n } = useTranslation();
  // A phone here is the studio's phone layout, with its fixed top bar and bottom navigation: up to MOBILE_MQ (899px), not
  // MOBILE (820px). There the area header names the level and the open list, Publish changes is an icon, the filter folds
  // and the footer sits over the navigation (the user, 2026-10-21: between 821 and 899px nothing named the open list).
  const isMobile = useMediaQuery(MOBILE_MQ);
  // Where the open list's search is drawn: its heading's row (see renderListHeading), set as that row mounts.
  const [filterSlot, setFilterSlot] = React.useState<HTMLDivElement | null>(null);
  // The brand's own lists (Add list, their chips, the editor, their place on the page) ship behind their own
  // flag. Gated off, none of it is read or shown, and the tab works as it did before own lists existed.
  const { isEnabled } = useFeatureGate();
  const ownListsOn = isEnabled(OWN_LISTS_FLAG);
  const listNamesOn = isEnabled('list-name-languages');
  const marketListsOn = isEnabled('market-lists');
  // FTUX - Getting Started's games piece: the open list plays its micro drag while the piece shows how (display only).
  const dragDemoOn = useDragDemoOn();
  // The page the owner picked. `page` below is the one shown: this, when the site has it.
  const [selectedPage, setSelectedPage] = React.useState<GamePage>(HOME_PAGE);
  // The list the owner picked on each page (falls back to the page's first list).
  const [picked, setPicked] = React.useState<Partial<Record<GamePage, string>>>({});
  // Loaded list contents and draft orders, keyed by data-source id: a list rendered on several pages is
  // one data source, so it shares one draft. The contents are the open page's only (see `contentsPage`).
  const [contents, setContents] = React.useState<Record<string, SiteSourceContent>>({});
  const [unreadable, setUnreadable] = React.useState<Record<string, boolean>>({});
  const [drafts, setDrafts] = React.useState<Record<string, string[]>>({});
  const [providerNames, setProviderNames] = React.useState<Record<string, string>>({});
  // The brand's tenant: what a list's name is translated for (see `saveCustomList`).
  const brandTenant = useAppSelector((state) => state.casinoInstance.displayBrand?.tenant_id);
  const tenantRaw = Number(brandTenant);
  const listsTenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  // The languages one of the owner's lists is named in (English, then the brand website's): what its heading offers
  // to edit one by one. Read once per brand, shared with the composers' Language sections.
  const siteLanguages = useSiteLanguages(ownListsOn && listNamesOn ? listsTenantId : undefined);
  const nameLanguages = React.useMemo(() => nameLanguagesOf(siteLanguages), [siteLanguages]);
  const [previewOpen, setPreviewOpen] = React.useState(false);
  const [device, setDevice] = React.useState<DeviceMode>('desktop');
  const [probeAttempt, setProbeAttempt] = React.useState(0);
  /**
   * The change bar HOLDS STILL while a tile is dragged: desktop reorder is realtime, so the order changes
   * under the pointer. Without this the bar would appear the moment a tile moved (before anything was
   * dropped) — and, once it was already showing, vanish at every drag start and jump the page under the
   * pointer. So whatever it was doing when the drag began is what it keeps doing until the drop.
   * `null` means no drag is in flight and the bar simply follows the page.
   */
  const [frozenBar, setFrozenBar] = React.useState<ChangeBarState | null>(null);
  // Whether a tile is being dragged right now. A reorder is stored once the drag ENDS, so a realtime
  // desktop drag is one write rather than one per pointer step.
  const [dragging, setDragging] = React.useState(false);
  const [writing, setWriting] = React.useState<'publish' | 'revert' | null>(null);
  // The custom-list editor: `new` builds one, `edit` opens the list with that id. Null while closed.
  //
  // An edit carries the list as it stood when the editor OPENED, frozen for as long as it stays open. The
  // editor seeds its own state from it once, so a baseline that moved underneath it (a reorder save landing
  // while it was open) made its change-diff read "reordered", enabled Save, and Save then wrote the
  // pre-reorder order back over the reorder that had just been stored.
  const [listEditor, setListEditor] = React.useState<
    { mode: 'new' } | { mode: 'edit'; id: string; initial: CustomListDraft } | null
  >(null);
  // The editor open now: a save from it reads it as it starts, and closes only that editor once stored (see
  // `saveCustomList`).
  const listEditorRef = React.useRef(listEditor);
  listEditorRef.current = listEditor;
  // Reorder lists: the page's lists to order, hide and show, applied on its Save order. Opened from the Website hub
  // (`?manage=1`): the tab's own way to order and hide them is its column of lists.
  const [manageOpen, setManageOpen] = React.useState(false);
  // The owner's own custom lists per page (Add List), as the service holds them. Loaded once per brand and
  // kept in step with every save and delete, so what the tab shows is what is stored.
  const [storedCustomLists, setCustomLists] = React.useState<Partial<Record<GamePage, CustomList[]>>>({});
  // The sources of the owner's lists deleted in this visit: the site frame keeps reporting what the page held
  // when it loaded, so these are still reported, and must not come back as site lists.
  const [deletedSources, setDeletedSources] = React.useState<ReadonlySet<string>>(() => new Set());
  // Whether the brand's own lists have been read. Until they are, the page's arrangement is not one to edit
  // or publish: its sections for them would read as lists the brand no longer has, and be dropped.
  const [ownListsLoaded, setOwnListsLoaded] = React.useState(false);
  // The last read of them failed: said on the tab, with Try again, until a read succeeds. A toast alone faded,
  // and nothing read them again, which left the page unarrangeable for the rest of the visit.
  const [ownListsFailed, setOwnListsFailed] = React.useState(false);
  // Whether the service has answered the read of them, either way. The chips wait for that answer (see
  // `listsIn`), and a failed read must not hold them for good.
  const [ownListsAnswered, setOwnListsAnswered] = React.useState(false);
  // What the rest of the tab goes by. Gated off, the lists read as loaded and empty (derived, never read), so
  // nothing waits on a read that will not happen.
  const ownLists = ownListsState(ownListsOn, ownListsLoaded, storedCustomLists);
  const customLists: Partial<Record<GamePage, CustomList[]>> = ownLists.lists;
  const ownListsReady = ownLists.ready;
  // A write to one of the owner's lists is in flight. ONE fact, used by every path that writes: the editor
  // shows it, the reorder save waits on it, and each refuses to start a second write while it holds. Two
  // flags were tried, one per path, and they could not see each other: an editor save and a reorder save
  // ran concurrently on the same list and the later response decided what was stored.
  //
  // State rather than a ref, on purpose. A ref cannot wake the reorder effect when a write finishes, and
  // that effect has to re-run then: a reorder made during the round trip sits in the draft waiting for it.
  const [writingList, setWritingList] = React.useState(false);
  // Bumped when a read is discarded as stale, to run it again. Dropping it without re-reading hid every
  // list except the one just written, which is the same end state the failed-read message exists to avoid.
  const [listsReload, setListsReload] = React.useState(0);
  // The order whose write failed, as `<list id>:<ids>`. Without it the reorder effect would retry a failing
  // write forever, because the draft it reads never changes. State rather than a ref because the screen
  // shows it: an order on screen that is not stored says so, with a retry, instead of looking saved.
  const [failedOrder, setFailedOrder] = React.useState('');
  // Bumped by every save and delete. The load below carries the value it started with, so a response that
  // was already in flight when a write landed is dropped instead of replacing the fresher state with the
  // snapshot it was taken from.
  const listWritesRef = React.useRef(0);
  // Each page's sections as the site serves them: a list's title for its chip name, which lists the page places,
  // and, for the page the service names, the lists the bridge waits for (see `pageSourcesLoaded`).
  const [sectionContents, setSectionContents] = React.useState<Record<string, SiteSourceContent>>({});
  const canPublish = Boolean(casinoInstanceId) && creativesServiceConfigured();

  const probeRef = React.useRef<HTMLIFrameElement>(null);
  // The page tree and the page beside it, brought back into view when a page is opened from far down (see `onOpenPage`).
  const columnsRef = React.useRef<HTMLDivElement>(null);
  // How wide the tab is, measured before it is painted and again whenever it changes (the window, the studio's side
  // panels). Too narrow for the page tree, the page's lists and the open list's games side by side, and on a phone, the
  // tab drills down (`drill`): the site's pages, then the open page's lists, then the open list's games, one level at
  // a time (`level`), each with the way back up. The level is kept as the owner moves on a wide desktop too (a page
  // opened is its lists, a list opened its games), so a window narrowed later shows what they were last at.
  const [tabWidth, setTabWidth] = React.useState<number | null>(null);
  React.useLayoutEffect(() => {
    const columns = columnsRef.current;
    if (!columns) return undefined;
    setTabWidth(columns.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(([entry]) => setTabWidth(entry.contentRect.width));
    observer.observe(columns);
    return () => observer.disconnect();
  }, []);
  const drill = isMobile || (tabWidth !== null && tabWidth < GAME_LISTS_COLUMNS_MIN_PX);
  // The open list's actions as icons alone: on a phone, and where its column is too narrow for its search beside them
  // with their labels.
  const compactActions = isMobile || (tabWidth !== null && tabWidth < GAME_LISTS_LABELLED_ACTIONS_MIN_PX);
  const [level, setLevel] = React.useState<'pages' | 'lists' | 'games'>('pages');
  // Drilled down, the FTUX's games and list pieces need the open page's games or its column of lists on screen (their
  // zones): the tab goes to that level while one plays, and stays there (the way back up a level brings it straight
  // back), or the piece's zone would go and the piece end with it.
  const ftuxGames = useGamesShown();
  const ftuxLists = useListsShown();
  React.useEffect(() => {
    if (!drill) return;
    if (ftuxGames && level !== 'games') setLevel('games');
    else if (!ftuxGames && ftuxLists && level !== 'lists') setLevel('lists');
  }, [drill, ftuxGames, ftuxLists, level]);
  // On a wide desktop the three columns reach down to the bottom of the screen, less what the studio's scroll area keeps
  // under the tab (24px at least), and each scrolls on its own: the page does not scroll at all. Their height
  // (`--game-panes-h`) is set on the tab before it is painted, and again as the screen or what is around the tab
  // changes: from where they start in the scroll area down to its bottom, then less whatever would still scroll there
  // (the area's own padding under the tab), read once the height is in.
  React.useLayoutEffect(() => {
    const columns = columnsRef.current;
    if (drill || !columns) return undefined;
    const measure = (): void => {
      const scroller = scrollAreaOf(columns);
      const viewTop = scroller ? scroller.getBoundingClientRect().top + scroller.clientTop : 0;
      const viewHeight = scroller ? scroller.clientHeight : window.innerHeight;
      const scrolled = scroller ? scroller.scrollTop : window.scrollY;
      const top = columns.getBoundingClientRect().top - viewTop + scrolled + GAME_LISTS_GAP_PX;
      let height = Math.max(GAME_LISTS_PANES_MIN_PX, Math.round(viewHeight - top - GAME_LISTS_GAP_PX));
      columns.style.setProperty('--game-panes-h', `${height}px`);
      const overflow = (scroller ? scroller.scrollHeight : document.documentElement.scrollHeight) - viewHeight;
      if (overflow > 0) {
        height = Math.max(GAME_LISTS_PANES_MIN_PX, height - overflow);
        columns.style.setProperty('--game-panes-h', `${height}px`);
      }
    };
    measure();
    window.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      observer?.disconnect();
      columns.style.removeProperty('--game-panes-h');
    };
  }, [drill]);
  // The open list's games on a wide desktop, scrolled inside their column: a list opened starts at its top.
  const gamesScrollRef = React.useRef<HTMLDivElement>(null);
  // Its heading, held over its top: how much of the column it covers (and its fade) is set on what scrolls there, so a
  // searched-for game lands under it (see HELD_HEAD_VAR). Measured again as the heading's row wraps or unwraps.
  const [gamesHead, setGamesHead] = React.useState<HTMLDivElement | null>(null);
  React.useLayoutEffect(() => {
    const scroller = gamesScrollRef.current;
    if (!gamesHead || !scroller) return undefined;
    const measure = (): void => scroller.style.setProperty(HELD_HEAD_VAR, `${gamesHead.offsetHeight + HEAD_FADE_PX}px`);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(gamesHead);
    return () => {
      observer?.disconnect();
      scroller.style.removeProperty(HELD_HEAD_VAR);
    };
  }, [gamesHead]);
  // False once this instance unmounts (a brand switch remounts GameLists), so a publish/revert that
  // resolves after the switch does not toast for the brand the owner already left. `useIsAlive` rather
  // than a ref cleared on cleanup: Fast Refresh and StrictMode run that cleanup on a component that stays
  // mounted, and the ref never came back, which left `writingList` stuck and every write path dead.
  const isAlive = useIsAlive();
  // The service's answer for the site when one is in and still fresh (read by an earlier visit of the tab, by the
  // Website hub's card, or by the warm-up once the brand settled): the tab opens with it, its pages decided at its
  // first draw. Peeked once, at mount, and everything the answer gives comes from that one peek: the pages, the site's
  // language, whether the service read the site's navigation, and the end of the read the tab would start
  // (`sitePagesKey`, for the same brand, host and first attempt). Opened with the pages but not that end, the read
  // would stay pending, and the chips would wait for it for good.
  const [seed] = React.useState(() => {
    const host = hostOf(siteUrl);
    const answer =
      casinoInstanceId && host && creativesServiceConfigured() ? peekSitePages(casinoInstanceId, host) : undefined;
    return answer ? { answer, key: sitePagesKeyOf(casinoInstanceId, host, 0) } : undefined;
  });
  // The pages of the brand's site that show game lists, as the service reads them from the site's own config.
  // Until they are in, or when the service cannot say, the tab offers the home page alone: every brand has one.
  const [sitePages, setSitePages] = React.useState<SitePage[] | null>(seed?.answer.pages ?? null);
  // The site's own language, as the service reads it from the site: its pages and lists are named in it (their link
  // texts and section titles), the way its players read them. The hub's language when the service names none.
  const [siteLanguage, setSiteLanguage] = React.useState(seed?.answer.language ?? '');
  // Whether the service read every navigation the site renders, so that its word on which pages they link decides the
  // pages the tab offers (see `serviceDecides`). False from a service that does not say.
  const [linkedKnown, setLinkedKnown] = React.useState(seed?.answer.linkedKnown ?? false);
  const textLanguage = siteLanguage || i18n.language;
  // What the site shows for one of its texts: it passes its titles and link texts through its translation table, so a
  // Mexican site whose sidebar names a list "Crash Games" shows "Juegos Crash". Only in the language the site is read in.
  // The page tree waits for that table before it names the site's pages (see `namesHeld`).
  const { table: siteTable, settled: siteTableSettled } = useSiteTranslations(siteLanguage);
  const siteWords = React.useCallback((text: string): string => siteText(siteTable, text), [siteTable]);
  // Whether the site's words are in: its translation table is (or its read failed, or there is none to read), else
  // SITE_WORDS_WAIT_MS has gone by since its language was named. The page tree (`namesHeld`) and the chips drawn on the
  // service's word (`listsIn`) wait for them, so neither names a page or a list in the sources' words and then renames
  // it.
  const [siteWordsWaited, setSiteWordsWaited] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (siteTableSettled || siteWordsWaited === siteLanguage) return undefined;
    const timer = window.setTimeout(() => setSiteWordsWaited(siteLanguage), SITE_WORDS_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [siteTableSettled, siteWordsWaited, siteLanguage]);
  const siteWordsIn = siteTableSettled || siteWordsWaited === siteLanguage;
  const siteHost = React.useMemo(() => hostOf(siteUrl), [siteUrl]);
  // The market's collections a new list may start from, read for the brand's site when the flag is on (one read shared
  // by every opening of the wizard, see getMarketCollectionsShared).
  const loadMarketCollections = React.useMemo(
    () =>
      marketListsOn && casinoInstanceId && siteHost !== '' && creativesServiceConfigured()
        ? () => getMarketCollectionsShared(casinoInstanceId, siteHost)
        : undefined,
    [marketListsOn, casinoInstanceId, siteHost],
  );
  // How the read of the site's pages ended, for the read it was (brand, host, attempt): a failed read is not a site
  // without pages, so "No game lists on this page" never stands on one (see `pageListsVerdict`). Try again reads them
  // again by bumping the attempt.
  const [sitePagesAttempt, setSitePagesAttempt] = React.useState(0);
  const sitePagesAsked = Boolean(casinoInstanceId) && siteHost !== '' && creativesServiceConfigured();
  const sitePagesKey = sitePagesKeyOf(casinoInstanceId, siteHost, sitePagesAttempt);
  const [sitePagesEnd, setSitePagesEnd] = React.useState<{ key: string; read: SitePagesRead } | null>(() =>
    seed ? { key: seed.key, read: 'ok' } : null,
  );
  const sitePagesRead: SitePagesRead = !sitePagesAsked
    ? 'unavailable'
    : sitePagesEnd?.key === sitePagesKey
      ? sitePagesEnd.read
      : 'pending';
  // The preview's host, not the public domain every other call takes (see ../../site-host): the service reads
  // this site's own page to learn its site id, and off production the public domain is often not connected.
  // The answer is the one every reader of the brand's site shares (the Website hub's card, the warm-up): a fresh one is
  // not asked for again, a read in flight is joined, and leaving the tab leaves it to them. Nothing is asked once the
  // read has ended, as it has at mount when the tab opened with an answer (`seed`).
  React.useEffect(() => {
    if (!casinoInstanceId || !siteHost || !creativesServiceConfigured() || sitePagesEnd?.key === sitePagesKey)
      return undefined;
    let cancelled = false;
    void getSitePagesShared(casinoInstanceId, siteHost).then((result) => {
      if (cancelled) return;
      if (result.ok) {
        setSitePages(result.value.pages);
        setSiteLanguage(result.value.language);
        setLinkedKnown(result.value.linkedKnown);
      }
      setSitePagesEnd({
        key: sitePagesKey,
        read: result.ok ? 'ok' : result.reason === 'unavailable' ? 'unavailable' : 'failed',
      });
    });
    return () => {
      cancelled = true;
    };
  }, [casinoInstanceId, siteHost, sitePagesKey, sitePagesEnd?.key]);
  // The tab offers the home page first, then the site's other pages that show game lists, in the service's order:
  // pages of sections (the Casino page, for one), whose lists can be arranged too, and single-list category pages,
  // whose games can be reordered. The service's answer is what names each page's source.
  const pages = React.useMemo<SitePage[]>(
    () => [
      sitePages?.find((p) => p.path === HOME_PAGE) ?? {
        path: HOME_PAGE,
        kind: 'sections',
        source_id: '',
        overrides_allowed: true,
      },
      // A list page of a list the platform serves wrongly is withheld, as its chip is (`isWithheldPlatformList`).
      ...(sitePages ?? []).filter(
        (p) => p.path !== HOME_PAGE && !(p.kind === 'list' && isWithheldPlatformList(p.source_id)),
      ),
    ],
    [sitePages],
  );
  // A page the site links to from nowhere is not offered: no player reaches it from the site's navigation (on
  // TalismanMax, /exclusive-games, /originals and /table-and-cards). A page is linked when the service names it with a
  // link's text; when the service says a navigation the site renders links it (`linked`), once it says it read every
  // one of them (`serviceDecides`), as the live site resolves them and before the site has booted in the frame; or when
  // a navigation the framed site reports links its path (read below: its sidebar, top bar, footers), which only ever
  // adds to the service's word. Fail open: without that word, nothing is hidden until the navigation a page reports has
  // been read in full (and there was some), nor while a read of it answered nothing (until it is read); and nothing is
  // hidden before the owner's own lists are in. Once the tree has been drawn decided, the decision stands
  // (`decidedDrawn`): a navigation source a later page reports whose read answers nothing does not open it to every
  // page again. The linked paths only grow over the session, so a page a later read links joins it. The home page, the
  // page the owner is on with the page it sits under and a page holding one of the owner's lists always stay
  // (`keptWithoutFrame`); a list's own page stays with the page it sits under, or when the navigation links it itself
  // (see `offeredSitePages`). And a page once drawn in the tree stays for the visit, fail open too (`drawnPages`): a
  // decision that came after the tree was drawn, or the owner leaving a page nothing else keeps, never takes a page back
  // from under them.
  const [linked, setLinked] = React.useState<{ paths: ReadonlySet<string>; known: boolean }>(() => ({
    paths: new Set(),
    known: false,
  }));
  // The navigation sources read (each once, and never unread again), and those whose last read answered nothing while
  // their frame was still there (asked again at the next report; while any is, nothing is hidden, unless the pages
  // were already decided).
  const [navRead, setNavRead] = React.useState<{ read: ReadonlySet<string>; unread: ReadonlySet<string> }>(() => ({
    read: new Set(),
    unread: new Set(),
  }));
  // Every page the tree has drawn in this visit (recorded while rendering, see `pageTabsSettled`), so a page once drawn
  // is never taken back.
  const [drawnPages, setDrawnPages] = React.useState<ReadonlySet<string>>(() => new Set());
  // Whether the pages the tab offers have been decided in this visit (recorded while rendering, with `drawnPages`),
  // the tree drawn or still held for the site's words (`namesHeld`): once decided, they stay so.
  const [decidedDrawn, setDecidedDrawn] = React.useState(false);
  // The top page the owner is on, or the one the list page they are on sits under, with all its list pages.
  const selectedTop = React.useMemo(
    () => groupOf(selectedPage, groupSitePages(pages))?.page.path ?? selectedPage,
    [pages, selectedPage],
  );
  // The pages that stay whatever the site's navigation links: the home page, the page the owner is on with the page it
  // sits under, a section or category page the service names with a link's text, and a page holding one of the owner's
  // own lists.
  const keptWithoutFrame = React.useCallback(
    (p: SitePage): boolean =>
      p.path === HOME_PAGE ||
      p.path === selectedPage ||
      p.path === selectedTop ||
      (p.kind !== 'list' && p.label !== undefined) ||
      (customLists[p.path]?.length ?? 0) > 0,
    [selectedPage, selectedTop, customLists],
  );
  // Whether the pages the tab offers are decided: by the service's word when it read every navigation the site renders
  // (in at the tab's first draw when an answer read before is still fresh, see `seed`), else once the navigation the
  // site reported is read in full; either way, once the owner's own lists are in (see the rule above, and
  // `pageTabsSettled`).
  const serviceDecides = sitePagesRead === 'ok' && linkedKnown;
  const pagesDecided = ownListsReady && (serviceDecides || (linked.known && navRead.unread.size === 0));
  // The pages drawn before are added to the decided ones, not kept by the rule: kept, a top page drawn only as the
  // page a linked list page sits under would bring in its other list pages, which `offeredSitePages` holds back.
  const visiblePages = React.useMemo(() => {
    if (!pagesDecided && !decidedDrawn) return pages;
    const offered = new Set(
      offeredSitePages(
        pages,
        (p) => keptWithoutFrame(p) || (serviceDecides && p.linked === true) || linked.paths.has(p.path),
      ).map((p) => p.path),
    );
    return pages.filter((p) => offered.has(p.path) || drawnPages.has(p.path));
  }, [pages, pagesDecided, decidedDrawn, serviceDecides, linked.paths, keptWithoutFrame, drawnPages]);
  // The page tree's branches: the section and category pages, each with its single-list pages under it.
  const pageGroups = React.useMemo(() => groupSitePages(visiblePages), [visiblePages]);
  const page: GamePage = visiblePages.some((p) => p.path === selectedPage) ? selectedPage : visiblePages[0].path;
  const currentPage = visiblePages.find((p) => p.path === page) ?? visiblePages[0];
  const pageGroup = groupOf(page, pageGroups) ?? pageGroups[0];
  // A list's rows are read per page: the same list can be served differently on two pages (a carousel's first
  // rows on one, the whole list on its own page), so what was read on one page is never shown on another.
  // Reset while rendering, not in an effect, so the page switch never draws the previous page's rows.
  const [contentsPage, setContentsPage] = React.useState<GamePage>(page);
  if (contentsPage !== page) {
    setContentsPage(page);
    setContents({});
    setUnreadable({});
  }
  // The page of sections a list page's route shows under its list, after the list's providers (see
  // `SitePage.pageSourceId`): where the owner's lists go on a list page. The page's own list stays first, outside it.
  const listPageSections = currentPage.kind === 'list' ? currentPage.pageSourceId ?? '' : '';
  // Whether the owner can arrange this page's lists (order, Hide, their own lists; `pageArrangeable`): a page of
  // sections whose source allows the brand's override, or a category page whose navigation does (its links are its
  // lists; where the service cannot rewrite their order it offers none of them, and the owner's lists are added after
  // the site's), or a list page with a page of sections under its list (the lists under it; its own list keeps the
  // top).
  const arrangeable = pageArrangeable(currentPage);
  // A category page's links that its arrangement does not name keep their place (see `resolveArrangement`).
  const anchoredPage = currentPage.kind === 'category';
  // The section source the service names for a page of sections, or under a list page's list. The page may load and
  // report another page's sections too, so this, and not the first page source it reports, is the one its lists are
  // read from.
  const namedSections = currentPage.kind === 'sections' ? currentPage.source_id : listPageSections;
  // The bridge stops asking once its condition holds (`sourcesHeld`, see `pageSourcesLoaded`), and does not ask that
  // page again. The condition is built further down, from the lists the service names on the page, which wait for the
  // page's arrangement, itself read from what the bridge reports; the bridge checks it on every report of the site's
  // sources, so it is handed the last one built.
  const sourcesHeld = React.useRef<(sources: DsiSourceInfo[]) => boolean>(() => false);
  const sourcesLoaded = React.useCallback((reported: DsiSourceInfo[]): boolean => sourcesHeld.current(reported), []);
  const frameSrc = React.useMemo(() => previewFrameSrc(siteUrl, page), [siteUrl, page]);
  const { status, sources, sourcesSettled, sourcesDone, onFrameLoad, getSource } = useSiteBridge(
    frameSrc,
    probeRef,
    sourcesLoaded,
    probeAttempt,
  );
  // The load of the probe the bridge's word is for. Reloading the probe (Try again, a read-back after a publish) gives
  // the frame a new key, and until the reloaded page loads, the bridge still says what the last load said: that it has
  // stopped asking, say, which would take the open list for one the site never reported at once. Taken up as the
  // reloaded page loads (the bridge leaves `ready` then).
  const [bridgeLoad, setBridgeLoad] = React.useState(probeAttempt);
  if (bridgeLoad !== probeAttempt && status !== 'ready') setBridgeLoad(probeAttempt);
  const sourcesDoneNow = sourcesDone && bridgeLoad === probeAttempt;
  // Whether the sources are the reloaded site's own. The bridge keeps the last load's until the reloaded site reports
  // (a reload with the same page clears nothing), so what they name then is no word of this load's: the sources held
  // when the probe was last reloaded are taken as the last load's until a report replaces them.
  const [sourcesAtReload, setSourcesAtReload] = React.useState({ attempt: probeAttempt, sources });
  if (sourcesAtReload.attempt !== probeAttempt) setSourcesAtReload({ attempt: probeAttempt, sources });
  const reportedThisLoad = probeAttempt === sourcesAtReload.attempt && sources !== sourcesAtReload.sources;
  // The frame as it is now, for a read that answers after it changed: one asked of a frame that went away (a page
  // switch reloads it) comes back empty, and that is no answer of the site's.
  const frameNow = React.useRef({ src: frameSrc, ready: status === 'ready' });
  frameNow.current = { src: frameSrc, ready: status === 'ready' };

  // The tenant-0 catalogue, which is how a stored list's game IDS become tiles. Read SLIM (no art: the tiles read
  // theirs by id, see GameListGrid's `artById`). Module-cached per brand and shared with the list editor, so opening
  // the editor afterwards costs nothing.
  //
  // Fetched only once this brand turns out to HAVE a list of its own. It holds every game there is, nothing else
  // on this tab reads it (the preview reads the full one, see `useFullCatalogue`), and a brand with no lists of its
  // own (every brand, until it makes one) would otherwise pay for it on every visit to Games.
  const hasOwnLists = React.useMemo(
    () => Object.values(customLists).some((lists) => (lists?.length ?? 0) > 0),
    [customLists],
  );
  const [catalogAttempt, setCatalogAttempt] = React.useState(0);
  const {
    catalog,
    loading: catalogLoading,
    failed: catalogReadFailed,
  } = useGameSources('all', hasOwnLists, catalogAttempt);

  // The page's section source, as the page reports it: the one the service names, when it names one. None while the
  // service has yet to answer: the first page source reported may be another page's (see `sourcesLoaded`).
  const sectionsId = sitePagesRead === 'pending' ? null : sectionSourceId(sources, namedSections);
  // The service's word for the page's source, else what the page reports (when the service could not say). On a list
  // page, the page of sections under its list.
  const arrangementSourceId = arrangeable ? listPageSections || currentPage.source_id || sectionsId : null;
  // The page's arrangement: which lists it shows, in what order, and which of them are hidden. Reorder lists
  // edits it, and like a list's game order it is a draft until the change bar publishes it. The page under a list page's
  // list may not exist yet: the service reads it as empty for the site named (see `SitePage.pageSourceId`).
  const arrangement = usePageArrangement({
    sourceId: arrangementSourceId,
    casinoInstanceId,
    domain: listPageSections ? siteHost : undefined,
  });
  // The lists the page shows that its arrangement does not place come first, where the page draws them: a list page's
  // own list, and a category page's links when the service does not offer them.
  const unplacedFirst =
    currentPage.kind === 'list' || (currentPage.kind === 'category' && arrangement.platform.length === 0);
  // Whether the platform has made the page under a list page's list with sections of its own on it, as that page's
  // arrangement says once read: the site reports that page then, and the lists its sections show a round trip after it
  // (see `bridgeSections`).
  const platformUnderList = listPageSections !== '' && arrangement.platform.length > 0;

  // The site's own lists. A page showing one of the brand's own reports its source too, and that must not
  // become a second chip beside the owner's, nor linger as a site list once the owner has deleted it. A
  // list's source id says nothing of whose it is (it is a slug of its name), so the brand's are named: every
  // list it has, every one its page arrangement names (known before the lists load), and every one deleted
  // since the page was probed.
  const ownSourceIds = React.useMemo(() => {
    const ids = new Set(deletedSources);
    for (const lists of Object.values(customLists)) for (const list of lists ?? []) ids.add(list.sourceId);
    for (const arr of [arrangement.published, arrangement.draft])
      for (const row of arr.order) if (row.owner) ids.add(row.owner.list_id);
    return ids;
  }, [deletedSources, customLists, arrangement.published, arrangement.draft]);
  // The lists of a page of sections as the service reads the page, the way the site does (`SitePage.lists`, the brand's
  // override applied): known as soon as the service answers, long before the site has booted in the frame. Their game
  // lists, but the brand's own (which have chips of their own; an older one goes by its prefix, see `siteGameLists`),
  // name and order the page's chips (`servicePath`), and their states are read early; the chips are drawn once the site
  // has reported the page too (see `siteAnswered`), with the lists it adds that the service did not name, and each
  // list's games are read once the site reports the list. Where the
  // service did not read the page that way (another kind of page, a service from before `lists`, a read that failed)
  // the chips wait for the frame, as they always did.
  const serviceLists = sitePagesRead === 'ok' && currentPage.kind === 'sections' ? currentPage.lists : undefined;
  const serviceIds = React.useMemo<string[] | null>(
    () =>
      serviceLists && currentPage.source_id !== ''
        ? serviceListIds(serviceLists).filter((id) => isSiteGameListId(id, ownSourceIds))
        : null,
    [serviceLists, currentPage.source_id, ownSourceIds],
  );
  const servicePath = canPublish && serviceIds !== null && serviceIds.length > 0;
  // The site's lists the frame has reported (with games to show): the only ones whose games can be read from it.
  const reportedIds = React.useMemo(() => new Set(gameListsFromSources(sources).map((list) => list.id)), [sources]);
  const sectionsContent = sectionsId ? sectionContents[sectionsId] : undefined;
  // The lists a page of sections places: what its sections show as the site serves them, the lists the service names on
  // it (see `servicePath`), and what its arrangement names (a list the owner hid is not served, and its chip is how they
  // bring it back).
  const placedOnPage = React.useMemo(() => {
    const ids = new Set(sectionsContent ? sectionListIds(sectionsContent) : []);
    if (servicePath && serviceIds) for (const id of serviceIds) ids.add(id);
    if (arrangement.status === 'ready')
      for (const arr of [arrangement.published, arrangement.draft])
        for (const row of pageListRows(resolveArrangement(arrangement.platform, arr, anchoredPage))) ids.add(row.listId);
    return ids;
  }, [
    sectionsContent,
    servicePath,
    serviceIds,
    arrangement.status,
    arrangement.published,
    arrangement.draft,
    arrangement.platform,
    anchoredPage,
  ]);
  // The links the site's navigation opens each list by, to name the chips of a category or list page: each
  // category page's own link list (read from the site once, on that page) and the list pages the service
  // names. A category page's first link is itself ("Home"), so the links to the section pages are left out.
  const [categoryLinkContents, setCategoryLinkContents] = React.useState<Record<string, SiteSourceContent>>({});
  const categoryLinksId =
    currentPage.kind === 'category' && sources.some((s) => s.id === currentPage.source_id) ? currentPage.source_id : '';
  const categoryLinksRead = categoryLinksId !== '' && categoryLinksId in categoryLinkContents;
  React.useEffect(() => {
    if (status !== 'ready' || !categoryLinksId || categoryLinksRead) return undefined;
    let cancelled = false;
    void getSource(categoryLinksId).then((value) => {
      const read = asSourceContent(value);
      if (!cancelled && read) setCategoryLinkContents((prev) => ({ ...prev, [categoryLinksId]: read }));
    });
    return () => {
      cancelled = true;
    };
  }, [status, categoryLinksId, categoryLinksRead, getSource]);
  // The site's navigation, as read from the framed site (see the reads below), and what it calls each path it links to.
  const [navContents, setNavContents] = React.useState<SiteSourceContent[]>([]);
  const navLabels = React.useMemo(
    () =>
      Object.fromEntries(
        Object.entries(navigationLabels(navContents, textLanguage)).map(([path, text]) => [path, siteWords(text)]),
      ),
    [navContents, textLanguage, siteWords],
  );
  // What the site's navigation calls a page, as the site shows it: the service's word for it (`navLabel`, read from
  // every navigation the site renders, so it names the page from the tree's first draw, before the site has booted in
  // the frame), else what the navigation read from the framed site calls it.
  const navWord = React.useCallback(
    (p: SitePage | undefined, path: GamePage): string | undefined => {
      const said = labelIn(p?.navLabel, textLanguage);
      return said !== null ? siteWords(said) : navLabels[path];
    },
    [textLanguage, siteWords, navLabels],
  );
  // A list page its page's menu does not link is named as the site's navigation names it, as the tree names it (see
  // `pageLabel`): its chip, its grid's heading and the preview go by the same name.
  const listLinks = React.useMemo<NavLink[]>(
    () => [
      ...navLinks(Object.values(categoryLinkContents), textLanguage).map((link) => ({
        ...link,
        text: siteWords(link.text),
      })),
      ...pages.flatMap((p) => {
        if (p.kind !== 'list') return [];
        const named = labelIn(p.label, textLanguage);
        const text =
          (p.inMenu === false ? navWord(p, p.path) : undefined) ?? (named !== null ? siteWords(named) : null);
        return text ? [{ path: p.path, text }] : [];
      }),
    ],
    [categoryLinkContents, pages, textLanguage, siteWords, navWord],
  );
  const sectionPaths = React.useMemo(
    () => new Set(pages.filter((p) => p.kind !== 'list').map((p) => p.path)),
    [pages],
  );
  // What the service holds for each list on the page (saved draft, published and live orders), read once per
  // list when the page opens, so a list reordered in an earlier session is marked before it is opened. The
  // open list's own state (from `useOrderedSource`, fresher) replaces its entry, and stays after a switch.
  const [snapshots, setSnapshots] = React.useState<Record<string, OrderedSourceSnapshot>>({});
  // The lists whose state could not be read: the info point does not wait for them (see `initialOrderVerdict`).
  const [statesUnread, setStatesUnread] = React.useState<ReadonlySet<string>>(() => new Set());
  // The site's lists this page shows. The frame's store also holds lists the page does not show (a menu's, another
  // component's), so a page of sections offers the lists it places, once that is known; a list page offers its own
  // list, then, when its route shows a page of sections under the list, the lists that page places (`placedOnPage`). On
  // the service's word (see `servicePath`) a page of sections offers the lists the service names first, in its order,
  // whether the frame has reported them yet or not, then the lists it places that the service did not name, as the
  // frame reports them. A list's count is its games as the service has them (its live order: a carousel loads only the
  // rows it shows), else its total when its content gives one, else the rows the site has loaded (none, for a list the
  // frame has yet to report).
  // On a category page, the site's lists its published arrangement hides: the site draws no carousel for one, so the
  // frame never reports it, yet each keeps a chip, marked hidden, the way to show it again. Only those: a list the
  // frame has simply not reported yet (it reports as the site loads) is not one, and must not be read as one.
  const awayIds = React.useMemo(() => {
    if (currentPage.kind !== 'category' || arrangement.status !== 'ready') return new Set<string>();
    const drawn = new Set(siteGameLists(sources, ownSourceIds).map((list) => list.id));
    return new Set(
      resolveArrangement(arrangement.platform, arrangement.published, true)
        .order.filter((row) => row.hidden && row.platform?.ds_id)
        .map((row) => row.platform?.ds_id ?? '')
        .filter((id) => !drawn.has(id) && !ownSourceIds.has(id) && isOfferedGameList(id)),
    );
  }, [currentPage.kind, sources, ownSourceIds, arrangement.status, arrangement.platform, arrangement.published]);
  const reportedLists = React.useMemo(() => {
    const all = siteGameLists(sources, ownSourceIds).map((list) => ({
      ...list,
      count: liveTotal(snapshots[list.id]) ?? sourceTotal(contents[list.id]) ?? list.count,
    }));
    // A list page's own list, then the lists the page of sections under it places.
    if (currentPage.kind === 'list')
      return [
        ...all.filter((list) => list.id === currentPage.source_id),
        ...(listPageSections
          ? all.filter((list) => list.id !== currentPage.source_id && placedOnPage.has(list.id))
          : []),
      ];
    if (currentPage.kind === 'sections' && servicePath && serviceIds) {
      const reported = new Map(all.map((list) => [list.id, list]));
      return [
        ...serviceIds.map((id) => reported.get(id) ?? { id, count: liveTotal(snapshots[id]) ?? 0 }),
        ...all.filter((list) => placedOnPage.has(list.id) && !serviceIds.includes(list.id)),
      ];
    }
    if (currentPage.kind === 'sections' && placedOnPage.size > 0) return all.filter((list) => placedOnPage.has(list.id));
    // A category page's chips follow its own navigation, not the order the frame happened to report them in
    // (which changes from one load to the next, and with it the chip opened by default).
    if (currentPage.kind === 'category')
      return [
        ...inNavOrder(all, listLinks, sectionPaths),
        ...[...awayIds].map((id) => ({ id, count: liveTotal(snapshots[id]) ?? 0 })),
      ];
    return all;
  }, [
    sources,
    ownSourceIds,
    snapshots,
    contents,
    currentPage,
    listPageSections,
    servicePath,
    serviceIds,
    placedOnPage,
    listLinks,
    sectionPaths,
    awayIds,
  ]);
  // The site's lists this page offers as chips: those it shows, but for one that is the whole catalogue under another
  // name (TalismanMax's "Todos"), which is not a list to arrange. A list's state says which, so each list waits for its
  // own: one the page reports after its chips are drawn, or whose state outlasts LIST_STATES_WAIT_MS, would otherwise
  // draw a chip and take it back. A list whose state could not be read is offered (the hub cannot tell), and so is every
  // list while the service has answered for none of the page's: a slow service keeps no list out for good. The lists
  // the chips were first drawn with on this visit (`heldLists`) stay offered as the states come in, but for one that
  // turns out to be the whole catalogue: a later state never takes a drawn chip back, nor the open list with it. A list
  // the service names that the frame has yet to report is offered on the service's word alone once its state says it
  // shows games: one whose state shows none, or could not be read, waits for the frame like any other.
  const [heldLists, setHeldLists] = React.useState<{ page: GamePage | null; ids: ReadonlySet<string> }>(() => ({
    page: null,
    ids: new Set(),
  }));
  const siteLists = React.useMemo(() => {
    const stated = (id: string): boolean => id in snapshots || statesUnread.has(id);
    const waitEach = canPublish && reportedLists.some((list) => stated(list.id));
    const held = heldLists.page === page ? heldLists.ids : null;
    return reportedLists.filter((list) => {
      if (snapshots[list.id]?.catalogue) return false;
      if (held?.has(list.id) === true) return true;
      if (servicePath && !reportedIds.has(list.id)) return liveTotal(snapshots[list.id]) !== null;
      return !waitEach || stated(list.id);
    });
  }, [reportedLists, snapshots, statesUnread, canPublish, heldLists, page, servicePath, reportedIds]);
  // The chips wait for the lists' states (how many games each has, whether one is the whole catalogue), so as not to
  // draw a count or a chip they would take back at once; but not for longer than LIST_STATES_WAIT_MS.
  const statesKnown =
    !canPublish ||
    !casinoInstanceId ||
    reportedLists.every((list) => list.id in snapshots || statesUnread.has(list.id));
  const [statesWaited, setStatesWaited] = React.useState<GamePage | null>(null);
  React.useEffect(() => {
    if (statesKnown || statesWaited === page) return undefined;
    const timer = window.setTimeout(() => setStatesWaited(page), LIST_STATES_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [statesKnown, statesWaited, page]);
  const statesIn = statesKnown || statesWaited === page;
  // The section source the bridge waits for (see `pageSourcesLoaded`): a page of sections' own, and under a list page's
  // list, the page there once its arrangement says the platform's own sections are on it (`platformUnderList`): the
  // site reports that page before the lists its sections show, and stopped at the page, it never reported them. Not a
  // page there that the platform has not made yet: the site never reports it, and waited for, it kept the bridge asking
  // until it gave up.
  const bridgeSections =
    currentPage.kind === 'sections' ? currentPage.source_id : platformUnderList ? listPageSections : '';
  // What the bridge waits for (see `pageSourcesLoaded`): the lists the sections it waits for place, as the site serves
  // them once they are read, else as they are named before that (`bridgeLists`): on the service's word (`servicePath`),
  // the lists the service names on a page of sections, and under a list page's list, the platform's game lists on the
  // page there, as its arrangement names them, those it hides among them (the section stays on the page, inactive, and
  // the site still fetches its list, see `serviceListIds`). And every list so named that the site has yet to report,
  // but for one whose state says it shows no games or is the whole catalogue (the site may never report it), so a chip
  // opened late is still read. A list whose state is not in yet is waited for. A service that read the page's lists as none
  // (`lists: []`, which can be out of step with the site) names nothing to wait for: taken for "any list", it let
  // another page's lists end the asking before the page's own were reported, so the page's sections decide then, as
  // without the service's lists.
  const bridgeContent = bridgeSections ? sectionContents[bridgeSections] : undefined;
  // The lists the sections the bridge waits for place (`bridgeSections`), as the site serves them, once read; null
  // until then, and on a page with no sections the bridge waits for.
  const sectionsPlaced = React.useMemo(() => (bridgeContent ? sectionListIds(bridgeContent) : null), [bridgeContent]);
  const bridgeLists = React.useMemo<string[] | null>(() => {
    if (servicePath && serviceIds) return serviceIds;
    if (!platformUnderList) return null;
    return [
      ...new Set(arrangement.platform.map((ref) => ref.ds_id).filter((id) => id !== '' && isOfferedGameList(id))),
    ];
  }, [servicePath, serviceIds, platformUnderList, arrangement.platform]);
  const bridgePlaced = React.useMemo(() => sectionsPlaced ?? bridgeLists, [sectionsPlaced, bridgeLists]);
  const awaitedLists = React.useMemo(
    () =>
      bridgeLists
        ? bridgeLists.filter(
            (id) => !(id in snapshots) || (liveTotal(snapshots[id]) !== null && !snapshots[id]?.catalogue),
          )
        : [],
    [bridgeLists, snapshots],
  );
  // What the bridge waits for is not known yet while the service has yet to answer (the Homepage is shown before it
  // does): the first page source reported may be another page's, and the name the service gives afterwards would never
  // be asked for. Nor on a list page with a page under its list while that page's arrangement is read, or after its read
  // failed, which says whether the bridge waits for that page (see `bridgeSections`): held on a guess, the bridge stopped
  // before the lists there were reported, and Try again read the page but asked the site nothing. Until it is known, the
  // condition never holds.
  const bridgeUnknown =
    sitePagesRead === 'pending' ||
    (listPageSections !== '' && (arrangement.status === 'loading' || arrangement.status === 'failed'));
  sourcesHeld.current = bridgeUnknown ? () => false : pageSourcesLoaded(bridgeSections, bridgePlaced, awaitedLists);
  // Whether the site has reported what the page tree's navigation decision needs (see the reads of the navigation
  // below): the page's own sources, as the bridge waited for them before it also waited for the lists named before the
  // site reports them (`awaitedLists`), the lists that count being the ones the sections it waits for place, once what
  // it waits for is known (`bridgeUnknown`); or the bridge done, whichever comes first. For the load the tab asked for,
  // as `sourcesDoneNow` is. A list the service names that the site never reports (hidden on that device, aimed at
  // players, or empty on the site) keeps the bridge asking for a minute and more: the tree, held for it, was drawn
  // undecided once PAGES_DECIDE_WAIT_MS ran out, with pages the site's menu does not link.
  const navSourcesIn =
    bridgeLoad === probeAttempt &&
    (sourcesDone ||
      (status === 'ready' && !bridgeUnknown && pageSourcesLoaded(bridgeSections, sectionsPlaced, [])(sources)));
  // The owner's lists on the page: those stored under its path and, on a list page with a page of sections under its
  // list, once that page's arrangement is read, every one stored under another path that it names (live or in its
  // draft). That page is named by the list's url segment, so every route showing the list shares it (betone has six on
  // one template), and the site offers the list at one of them, its first link, which can move (a menu edit, a
  // navigation read that failed): a list made on another of those routes is on this page all the same. Left out, it
  // had no chip, and its row was dropped from the page (`withOwnLists`), a change whose publish took it off the site.
  // Every write to one finds it where it is stored (`ownListById`, `withOwnListChanged`), and an edit saved from here
  // stores it under this page's path, in the service (`saveCustomList` saves with `page`) and in the tab: the path the
  // site offers now.
  const pageCustom = React.useMemo(() => {
    const here = customLists[page] ?? NO_OWN_LISTS;
    if (listPageSections === '' || arrangement.status !== 'ready') return here;
    const named = new Set<string>();
    for (const arr of [arrangement.published, arrangement.draft])
      for (const row of arr.order) if (row.owner) named.add(row.owner.list_id);
    const seen = new Set(here.map((custom) => custom.id));
    const elsewhere: CustomList[] = [];
    for (const [path, lists] of Object.entries(customLists)) {
      if (path === page) continue;
      for (const custom of lists ?? []) {
        if (!named.has(custom.sourceId) || seen.has(custom.id)) continue;
        seen.add(custom.id);
        elsewhere.push(custom);
      }
    }
    return elsewhere.length > 0 ? [...here, ...elsewhere] : here;
  }, [customLists, page, listPageSections, arrangement.status, arrangement.published, arrangement.draft]);
  // The site's lists plus the owner's own custom lists (Add List), each a chip.
  const lists = React.useMemo(
    () => [
      ...siteLists,
      // An automatic list counts what its filters match, once the catalogue that answers that is in. Until then, and
      // when it could not be read, it shows no count: its stored ids are only the owner's order over the matches.
      ...pageCustom.map((custom) => ({
        id: custom.id,
        count:
          custom.mode !== 'auto'
            ? custom.gameIds.length
            : catalog.length > 0
              ? ownListMembers(workingStateOf(custom, custom.gameIds), catalog).length
              : undefined,
      })),
    ],
    [siteLists, pageCustom, catalog],
  );
  // A custom list by id, so the chips, labels and reorder sheet can find the owner's own lists.
  const customById = React.useMemo(() => new Map(pageCustom.map((custom) => [custom.id, custom])), [pageCustom]);
  // ...and by the source the page renders it from, which is how the page's arrangement names it.
  const customBySource = React.useMemo(
    () => new Map(pageCustom.map((custom) => [custom.sourceId, custom])),
    [pageCustom],
  );
  const providerId = providerSourceId(sources);
  const pickedId = picked[page];
  const activeId =
    pickedId && lists.some((list) => list.id === pickedId)
      ? pickedId
      : lists[0]?.id;
  // The open list, when it is an owner's own custom list (its games are resolved from the catalogue; a site
  // list is read from the site into `contents`). A reorder of one is stored as a draft; publishing it is a
  // separate step, as for a site list.
  const activeCustom = activeId ? customById.get(activeId) : undefined;
  // The open list is one the site has yet to report on this load: named on the service's word (see `servicePath`), or
  // a chip held from the last load while the reloaded site has not reported it (or anything) yet. The site has nothing
  // to send for it until it has (the list still waits in its store).
  const activeUnreported =
    activeId !== undefined && !activeCustom && (!reportedThisLoad || !reportedIds.has(activeId));
  // The open custom list's games, resolved from the catalogue: a hand-picked list's ids (one the catalogue no
  // longer holds is a placeholder in its place, in `activeOwn.unavailable`), an automatic list's matches. Only
  // the OPEN list is resolved this way: the others need nothing but their count.
  const activeOwn = React.useMemo(
    () => (activeCustom ? ownListGames(workingStateOf(activeCustom, activeCustom.gameIds), catalog) : null),
    [activeCustom, catalog],
  );
  const activeCustomGames = React.useMemo<SiteGame[]>(() => activeOwn?.games ?? [], [activeOwn]);
  // A SETTLED catalogue is not a loaded one: `useGameSources` stops loading with an empty catalogue when
  // the fetch fails, deliberately, so its picker never hangs. Reading that as "the catalogue is empty"
  // here would render a stored list as having no games.
  const catalogUsable = !catalogLoading && catalog.length > 0;
  // The catalogue was asked for and came back unusable: its read failed, or settled with nothing in it. Worth naming
  // separately from "still loading": one of the owner's lists cannot be drawn at all without it, and without this the
  // tab sat on a loading skeleton for the rest of the session with no Edit and no Delete reachable either. Nor can an
  // automatic list be compared with what the site shows without it (see `gamesUnpublished`).
  const catalogFailed = hasOwnLists && (catalogReadFailed || (!catalogLoading && catalog.length === 0));
  // Memoised so the object identity is stable (a custom list synthesises its content), which the
  // content-reader effect and the order/games memos below depend on.
  //
  // While the catalogue is still loading the content stays UNDEFINED rather than becoming an empty list:
  // resolving against nothing yields no games, and a stored list would flash as empty before its tiles
  // arrived, which reads as the list having been lost.
  const content = React.useMemo<SiteSourceContent | undefined>(
    () =>
      activeCustom
        ? catalogUsable
          ? { data: activeCustomGames }
          : undefined
        : activeId
          ? contents[activeId]
          : undefined,
    [activeCustom, activeCustomGames, catalogUsable, activeId, contents],
  );
  const activePublishable = canPublish && !activeCustom;
  const hasProviderNames = Object.keys(providerNames).length > 0;
  // The page's own names for its lists (the titles its sections show): the service's, read as the site reads the page,
  // else the frame's, from its section source (already read into `sectionContents` above, which the Sections view also
  // uses). The service's win: in from the first draw, they are what the chips are drawn with, so no chip is renamed as
  // the frame's come in.
  const titles = React.useMemo(
    () => ({
      ...(sectionsContent ? listTitles(sectionsContent, textLanguage, siteWords) : {}),
      ...(serviceLists ? serviceListTitles(serviceLists, textLanguage, siteWords) : {}),
    }),
    [sectionsContent, serviceLists, textLanguage, siteWords],
  );
  // A list's name: an owner's own custom list by its name. Else, on a page of sections (or under a list page's list), the
  // title its section shows; on a category or list page, the words of the navigation link that opens it. Then a name read from
  // its id. The chips name one of the owner's lists by its own id, the preview (like the site) by its source id.
  const labelOf = React.useCallback(
    (listId: string): string => {
      const own = customById.get(listId)?.name ?? customBySource.get(listId)?.name;
      if (own) return own;
      const nav = listNavLabel(listId, listLinks, sectionPaths);
      if (currentPage.kind === 'sections' || listPageSections)
        return titles[listId] ?? nav ?? listLabel(listId, titles);
      return nav ?? listLabel(listId, titles);
    },
    [customById, customBySource, titles, listLinks, sectionPaths, currentPage.kind, listPageSections],
  );

  const { setDraft: setArrangementDraft, publish: publishArrangement, reload: reloadArrangement } = arrangement;
  const arrangementReady = arrangement.status === 'ready';
  const ownOnPage = React.useMemo<PageOwnList[]>(
    () =>
      pageCustom
        .filter((custom) => custom.sourceId !== '')
        .map((custom) => ({
          sourceId: custom.sourceId,
          name: custom.name,
          names: custom.names,
          kind: custom.kind,
          layout: custom.layout,
        })),
    [pageCustom],
  );
  // The page publishing would put on the site: the draft (else what is live) resolved against the platform's
  // page as it stands now, with every one of the brand's own lists for this page in it. Null until loaded, the
  // brand's own lists included: read without them, the page drops their sections (see `ownListsLoaded`).
  const workingPage = React.useMemo(
    () =>
      arrangementReady && ownListsReady
        ? arrangementWithOwnLists(
            ownListsOn,
            resolveArrangement(
              arrangement.platform,
              arrangement.draft.order.length > 0 ? arrangement.draft : arrangement.published,
              anchoredPage,
            ),
            ownOnPage,
          )
        : null,
    [
      arrangementReady,
      ownListsReady,
      ownListsOn,
      arrangement.platform,
      arrangement.draft,
      arrangement.published,
      ownOnPage,
      anchoredPage,
    ],
  );
  const livePage = React.useMemo(
    () => resolveArrangement(arrangement.platform, arrangement.published, anchoredPage),
    [arrangement.platform, arrangement.published, anchoredPage],
  );
  // A list made with Add List counts here from the moment it is stored: it is on the page once published.
  const pageChanged = workingPage !== null && !sameArrangement(workingPage, livePage);
  const pageRows = React.useMemo(() => (workingPage ? pageListRows(workingPage) : []), [workingPage]);
  // The data source a chip's list renders from, which is how the page names it: a site list's own id, and
  // the source of one of the owner's lists.
  const rowIdOf = React.useCallback(
    (chipId: string): string => customById.get(chipId)?.sourceId ?? chipId,
    [customById],
  );
  // The lists the page hides everywhere it would show them.
  const hiddenLists = React.useMemo(() => {
    const shown = new Set(pageRows.filter((row) => !row.hidden).map((row) => row.listId));
    return new Set(pageRows.filter((row) => !shown.has(row.listId)).map((row) => row.listId));
  }, [pageRows]);
  // The page's lists in page order. A list the page does not place (the site reports it, but no section shows it)
  // falls in after, in its natural order, or before (`unplacedFirst`).
  const orderedLists = React.useMemo(() => {
    const rank = new Map<string, number>();
    pageRows.forEach((row, i) => {
      if (!rank.has(row.listId)) rank.set(row.listId, i);
    });
    const unplaced = unplacedFirst ? -1 : pageRows.length;
    const at = (chipId: string): number => rank.get(rowIdOf(chipId)) ?? unplaced;
    return [...lists].sort((a, b) => at(a.id) - at(b.id));
  }, [lists, pageRows, rowIdOf, unplacedFirst]);
  // Every page draws a chip for each of its lists: a page with single-list pages its own lists (the single-list pages
  // are under it in the page tree, see `treeNodes`), a single-list page its own list, then, when its route shows a page
  // of sections under the list, the lists there, the owner's among them. (A list's chip is its row in the page's column
  // of lists, see `renderListsPanel`: the lists were drawn as chips before the column, and the word stayed with the
  // names that read it, `chipIds`, `chipSort` and the rest.)
  const drawnLists = orderedLists;
  // The column's rows (the user, 2026-10-21): one for each place the page shows a list, in page order, so a list shown
  // twice has two rows, each moved and hidden on its own, numbered to tell them apart; then the lists the page does not
  // place (the site reports them, no section shows them), first where `unplacedFirst` says, as `orderedLists` ranks them.
  // A row opens its list.
  const columnRows = React.useMemo((): ColumnRow[] => {
    const byRowId = new Map(drawnLists.map((list) => [rowIdOf(list.id), list] as const));
    const times = new Map<string, number>();
    pageRows.forEach((row) => times.set(row.listId, (times.get(row.listId) ?? 0) + 1));
    const seen = new Map<string, number>();
    const placed = pageRows.flatMap((row): ColumnRow[] => {
      const list = byRowId.get(row.listId);
      if (!list) return [];
      const n = (seen.get(row.listId) ?? 0) + 1;
      seen.set(row.listId, n);
      return [{ key: row.key, list, row, appearance: (times.get(row.listId) ?? 0) > 1 ? n : undefined }];
    });
    const unplaced = drawnLists
      .filter((list) => !times.has(rowIdOf(list.id)))
      .map((list): ColumnRow => ({ key: list.id, list, row: null }));
    return unplacedFirst ? [...unplaced, ...placed] : [...placed, ...unplaced];
  }, [drawnLists, pageRows, rowIdOf, unplacedFirst]);
  // Which of the open list's rows is the open one when the page shows it twice: the one pressed, else its first.
  const [pickedRow, setPickedRow] = React.useState<string | null>(null);
  const openRow = React.useMemo(() => {
    const ofOpen = columnRows.filter((entry) => entry.list.id === activeId);
    return ofOpen.find((entry) => entry.key === pickedRow)?.key ?? ofOpen[0]?.key ?? null;
  }, [columnRows, activeId, pickedRow]);

  // The chips are dragged into a new order up and down the column: the same move as Reorder lists makes (the sheet the
  // hub opens). The page follows the chips live, as the game grid does, and like any move it is a draft for the change
  // bar. Only a chip the page places can move.
  const chipIds = React.useMemo(() => columnRows.map((entry) => entry.key), [columnRows]);
  const handleChipMove = React.useCallback(
    (from: number, to: number): void => {
      const moved = columnRows[from];
      if (moved === undefined || moved.row === null || !workingPage || writing !== null) return;
      const next = chipIds.filter((key) => key !== moved.key);
      next.splice(to, 0, moved.key);
      // By the page's rows, so a list shown twice moves where it was taken from. A row the page does not place is
      // passed over (see listMoveFromOrder): an owner's list dropped among a category page's fixed lists goes back after
      // them.
      const move = listMoveFromOrder(
        pageRows.map((row) => row.key),
        next,
        moved.key,
      );
      if (move) setArrangementDraft(withListMoved(workingPage, move.from, move.to));
    },
    [columnRows, chipIds, workingPage, writing, pageRows, setArrangementDraft],
  );
  // Two places of one list are told apart only by their order on the page (the service gives them back to its sections
  // in turn), so neither is carried past the other: that would hand its state, hidden or shown, to the other place.
  const chipCanMove = React.useCallback(
    (from: number, to: number): boolean => {
      const listId = columnRows[from]?.row?.listId;
      if (listId === undefined) return false;
      const twins = columnRows.filter((entry) => entry.row?.listId === listId).map((entry) => entry.key);
      if (twins.length < 2) return true;
      const next = chipIds.filter((key) => key !== chipIds[from]);
      next.splice(to, 0, chipIds[from]);
      return next.filter((key) => twins.includes(key)).every((key, i) => key === twins[i]);
    },
    [columnRows, chipIds],
  );
  // The rows move as the games' rows do, on the same sorting hook (the user, 2026-10-20): by the grip at once (a mouse,
  // a finger, the arrow keys), by a mouse from anywhere on the row once it moves, by a finger resting on it anywhere,
  // its name too (`holdThrough`); a press on its eye is the eye's. The page scrolls while a row is held near the top or
  // bottom of what scrolls the rows (the column, on a wide desktop).
  const chipRows = useSortableRows({
    keys: chipIds,
    onMove: handleChipMove,
    canMove: chipCanMove,
    longPress: true,
    holdThrough: '[data-row-main]',
    autoScroll: drill ? SCREEN_CHROME[isMobile ? 'phone' : 'desktop'] : { top: 0, bottom: 0 },
  });
  // A row the page places moves. On a category page whose links the service does not offer, the site's lists stay
  // first, where the page draws them, and the owner's own lists move among themselves (the user, 2026-10-21).
  const chipDraggable = (entry: ColumnRow): boolean =>
    arrangeable &&
    entry.row !== null &&
    (currentPage.kind !== 'category' || arrangement.platform.length > 0 || entry.row.own) &&
    arrangementReady &&
    writing === null;
  // A row's eye hides that place of its list on the page, or shows it again: one the page places, on a page that can be
  // arranged, once its arrangement is in and nothing is being written.
  const canToggleHidden = (entry: ColumnRow): boolean =>
    arrangeable && arrangementReady && writing === null && entry.row !== null;

  // The active list's ordered tenant-source override (load, debounced draft autosave, publish, revert, and
  // the revert-availability flag), keyed by the brand's casino instance per the unified tenant-source
  // contract. Disabled (sourceId undefined) until there is a brand to publish for. Re-keys on the active
  // list, so switching lists re-reads its state.
  const source = useOrderedSource({
    kind: 'game_list',
    sourceId: activePublishable ? activeId : undefined,
    casinoInstanceId,
  });
  // Alias the two callables so the handler deps stay plain identifiers: exhaustive-deps treats a METHOD CALL
  // (source.setDraft(...)) as a dep on the whole object (a new identity each render), whereas the aliased
  // member is stable, so member calls get aliased; plain reads (source.status/draft) stay inline.
  const { setDraft: setSourceDraft } = source;

  // The section source whose last read got nothing: the chips stop waiting for its titles (see `listsIn`).
  const [sectionsUnread, setSectionsUnread] = React.useState<string | null>(null);
  // Reads the page's sections from the site the first time its section source is reported. A read that got
  // nothing (the frame was reloading) is not kept, so the source is read again when the page reports it.
  React.useEffect(() => {
    if (status !== 'ready' || !sectionsId || sectionsContent) return undefined;
    let cancelled = false;
    void getSource(sectionsId).then((value) => {
      if (cancelled) return;
      const content = asSourceContent(value);
      if (content) setSectionContents((prev) => ({ ...prev, [sectionsId]: content }));
      else setSectionsUnread(sectionsId);
    });
    return () => {
      cancelled = true;
    };
  }, [status, sectionsId, sectionsContent, getSource]);

  // Every list the page offers is in, and so is how the page names and orders them: the site's lists (read from
  // the framed page, seconds after the service has answered with the owner's), the owner's own, the page's
  // sections (its names for the site's lists, and which of them it places) and, on a page the owner arranges,
  // its arrangement (the chips' order, the owner's lists among the site's). Until then the tab stays on its
  // loading state. Drawn as each arrived, the owner's chips showed first and the site's popped in beside them
  // later, reshuffling the row and changing the open list under the owner.
  //
  // Each part counts once it has answered, either way, so a read that fails, or a page without that part, never
  // holds the chips. The site's lists are in once it reports some (its sections are often reported before them),
  // or once it has had its time (`sourcesSettled`). A page of sections also waits for the service's word on which
  // source is its own (`sitePagesRead`), as the page may report another page's sections first. On the service's word
  // (see `servicePath`) the site's part is in once a list the service names can be offered (see `siteLists`), framed or
  // not, and the chips are drawn once the site has answered as well (`siteAnswered`, the user's 2026-10-20 call: the
  // whole set at once, not lists popping in under a reorder), or failed. They are named by the service's titles
  // then, shown in the site's words, so however they are drawn they wait for those words too (`wordsIn`, see
  // `siteWordsIn`): drawn first, a chip was renamed as the site's translation table came in.
  //
  // Never in the render that switches pages: the bridge and the page's arrangement reset themselves in that render, but
  // still hand back the previous page's sources and arrangement, and the chips were shown on those. `contentsPage` is
  // still that page then.
  const ownListsIn = !ownListsOn || !canPublish || ownListsAnswered;
  // A list page's page under its list is waited for only when the platform's own sections are on it
  // (`platformUnderList`): the brand's lists there are known without the site.
  const sectionsAwaited = currentPage.kind === 'sections' || platformUnderList;
  const sectionsIn =
    !sectionsAwaited ||
    sectionsContent !== undefined ||
    (sectionsId !== null && sectionsUnread === sectionsId) ||
    (sourcesSettled && sectionsId === null && sitePagesRead !== 'pending');
  const arrangementIn =
    !canPublish ||
    !arrangementSourceId ||
    arrangement.status === 'ready' ||
    arrangement.status === 'failed' ||
    arrangement.status === 'unavailable';
  const frameListsIn =
    status === 'ready' &&
    // The site's own lists, not only the ones it hides (which are known before the site reports anything).
    (siteLists.some((list) => !awayIds.has(list.id)) || sourcesSettled) &&
    sectionsIn;
  const serviceListsIn = servicePath && siteLists.some((list) => serviceIds?.includes(list.id) === true);
  // Drawn on the service's word, the rows still wait for the site's own report (the page's sources and the lists it
  // places, or its quick poll for them run out), or for the site to fail: a list it reported after the rows were drawn
  // landed among them as the owner reordered them (the user, 2026-10-20, who chose the whole set over the service's head
  // start).
  const siteAnswered = status === 'failed' || (status === 'ready' && sourcesSettled && sectionsIn);
  const wordsIn = !servicePath || siteWordsIn;
  const listsIn =
    contentsPage === page &&
    (servicePath ? (serviceListsIn || frameListsIn) && siteAnswered : frameListsIn) &&
    wordsIn &&
    ownListsIn &&
    arrangementIn &&
    statesIn;
  // Once drawn on a page, the chips stay: a later read (the frame reloading, the page read again after a delete)
  // must not take the tab back to its loading state under the owner. Set while rendering, as `contentsPage` is.
  //
  // They are drawn with the first chip's list open, and it is held as picked, so a later move of the chips does
  // not open another list under the owner. Left to its fallback, the open list was the site's first, wherever
  // its chip stood in the column.
  const [listsShownOn, setListsShownOn] = React.useState<GamePage | null>(null);
  if (listsIn && listsShownOn !== page) {
    setListsShownOn(page);
    setHeldLists({ page, ids: new Set(siteLists.map((list) => list.id)) });
    const firstChip = orderedLists[0]?.id;
    if (firstChip && !picked[page]) setPicked((prev) => ({ ...prev, [page]: firstChip }));
  }
  const listsShown = listsIn || listsShownOn === page;
  // The chips the service may still draw with the site unreachable: its pages are not read yet, or they name the page's
  // lists and what the chips wait for on its word is not in. While so, and for SERVICE_HOLD_MS at most (a read that
  // never ends must not keep the tab loading), a site that could not be reached is not said yet: said, it would be taken
  // back for the chips a moment later.
  const serviceMayDraw =
    canPublish &&
    sitePagesAsked &&
    (sitePagesRead === 'pending' || (servicePath && !(wordsIn && ownListsIn && arrangementIn && statesKnown)));
  const failureKey = `${page}#${probeAttempt}`;
  const [failureHeldOut, setFailureHeldOut] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (status !== 'failed' || !serviceMayDraw || failureHeldOut === failureKey) return undefined;
    const timer = window.setTimeout(() => setFailureHeldOut(failureKey), SERVICE_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [status, serviceMayDraw, failureHeldOut, failureKey]);
  const failureHeld = status === 'failed' && serviceMayDraw && failureHeldOut !== failureKey;
  // What the page says while it offers no chips (see `pageListsVerdict`): "No game lists on this page" only once a read
  // that completed says so, never because the bridge's first poll ran out (a brand's site can report its lists later);
  // and a read that failed says the page's lists could not be loaded, rather than that it has none.
  const listsReadFailed =
    sitePagesRead === 'failed' ||
    (arrangeable && arrangement.status === 'failed') ||
    (ownListsOn && canPublish && ownListsFailed);
  const noChipsVerdict = pageListsVerdict({
    connected: status === 'ready',
    siteSettled: sourcesSettled,
    siteDone: sourcesDone,
    structure: pageStructure({
      kind: currentPage.kind,
      placedLists: placedOnPage.size,
      sectionsRead: sectionsContent !== undefined,
      arrangementIn,
      sitePages: sitePagesRead,
      listed: sitePages?.some((p) => p.path === page) ?? false,
    }),
    readFailed: listsReadFailed,
  });
  // The Website hub's deep links: `?add=list` opens the list editor to make one, `?manage=1` opens Reorder lists.
  // Acted on once, when the chips and the page's arrangement are in (a sheet opened earlier would show a page still
  // loading), then removed from the address (history replace, keeping the entry's router state: the hub's mark, which
  // the back button pops back to the hub by), so a reload never reopens a sheet. `add=list` without the brand's own lists
  // opens nothing, and is removed all the same (dropped, not kept: own lists switched on later do not open it). So is
  // either parameter on a page the owner cannot arrange: neither Add list nor a way to reorder is offered there (a list
  // made there could never take a place on the page, so it would never reach the site), and the page has no
  // arrangement to wait for. So is any other value of either parameter, which is not a link.
  const location = useLocation();
  const navigate = useNavigate();
  const deepLinkHandled = React.useRef(false);
  const deepLinkReady = listsShown && (workingPage !== null || !arrangeable);
  React.useEffect(() => {
    const link = gamesDeepLink(location.search);
    // Neither parameter is in the address: nothing to act on or remove. The one acted on has gone, so the next one the
    // hub opens (the tab still mounted) is acted on too.
    if (link.search === location.search) {
      deepLinkHandled.current = false;
      return;
    }
    if (!deepLinkReady || deepLinkHandled.current) return;
    deepLinkHandled.current = true;
    if (link.manage && arrangeable) setManageOpen(true);
    if (link.add && ownListsOn && arrangeable) setListEditor({ mode: 'new' });
    navigate(
      { pathname: location.pathname, search: link.search, hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [deepLinkReady, location, ownListsOn, arrangeable, navigate]);

  // The lists already asked about (a failed read is dropped from it, so it is asked again later).
  const askedRef = React.useRef(new Set<string>());
  // The site's lists the page shows by its own word, known before the site has booted in the frame and reported any: a
  // list page's own list, else the lists the service names on the page (see `servicePath`); and every one the page's
  // arrangement names (one it hides too), once that is in, which on a list page is the page of sections under its list
  // (none without one). Their states are asked for then, so they are read while the site boots, not after it. Only the
  // lists the site reports and those the service names (`reportedLists`) offer a chip (`siteLists`), hold the chips for
  // their states (`statesKnown`) or count for the info point. A list the service names draws a chip before the site
  // reports it, but only once its state is in and says it shows games and is not the whole catalogue; a list the
  // arrangement alone names draws nothing.
  const expectedLists = React.useMemo<string[]>(() => {
    const ownList = isOfferedGameList(currentPage.source_id) ? [currentPage.source_id] : [];
    // A list page's arrangement is the page of sections under its list, when it has one (else it reads none).
    const named = currentPage.kind === 'list' ? ownList : servicePath && serviceIds ? serviceIds : [];
    if (arrangement.status !== 'ready') return named;
    return [
      ...new Set([
        ...named,
        ...pageListRows(resolveArrangement(arrangement.platform, arrangement.published, anchoredPage))
          .filter((row) => !row.own && isOfferedGameList(row.listId))
          .map((row) => row.listId),
      ]),
    ];
  }, [
    currentPage.kind,
    currentPage.source_id,
    servicePath,
    serviceIds,
    arrangement.status,
    arrangement.platform,
    arrangement.published,
    anchoredPage,
  ]);
  // Only SITE lists have a tenant source to read; the owner's own lists are stored here, not on the site. All the site
  // reports for the page, a whole-catalogue one too (its state is what says it is one), and every one it names itself.
  const listKey = [...new Set([...reportedLists.map((list) => list.id), ...expectedLists])].join(' ');
  // Each list is asked once. A read still out when the lists change (the site reporting them, in an order of its own) is
  // not cut short and asked again, which would throw away the head start the page's arrangement gave it. Its answer is
  // dropped only once the tab has gone (a brand switch remounts it). Asked through the shared read, so a state another
  // reader read within the minute (the Website hub's Game Lists card) is not asked for again.
  React.useEffect(() => {
    if (!canPublish || !casinoInstanceId) return;
    const asked = askedRef.current;
    for (const listId of listKey.split(' ')) {
      if (!listId || asked.has(listId)) continue;
      asked.add(listId);
      void getTenantSourceStateShared(casinoInstanceId, { kind: 'game_list', source_id: listId }).then((result) => {
        if (!isAlive()) return;
        if (!result.ok) {
          asked.delete(listId);
          setStatesUnread((prev) => (prev.has(listId) ? prev : new Set(prev).add(listId)));
          return;
        }
        // A list opened meanwhile already holds a fresher state: this page-open read does not replace it.
        const loaded = snapshotFromState(result.value);
        setSnapshots((prev) => (listId in prev ? prev : { ...prev, [listId]: loaded }));
      });
    }
  }, [canPublish, casinoInstanceId, listKey, isAlive]);

  // Reads the provider names from the site once they are available.
  React.useEffect(() => {
    if (status !== 'ready' || !providerId || hasProviderNames) return undefined;
    let cancelled = false;
    void getSource(providerId).then((value) => {
      if (!cancelled) setProviderNames(providerNamesFromContent(asSourceContent(value)));
    });
    return () => {
      cancelled = true;
    };
  }, [status, providerId, hasProviderNames, getSource]);

  // Reads the active list's content from the site the first time it is shown.
  //
  // An owner's own list is skipped: the site has never heard of its id, so the bridge would wait out its
  // timeout and then mark a perfectly good stored list unreadable. This used to be covered by `content`
  // always being truthy for such a list; it is not, now that the games are resolved from the catalogue.
  React.useEffect(() => {
    // A list the site does not draw (see `awayIds`) has nothing to read there. Nor is it read while the probe reloads
    // (`bridgeLoad`): the bridge still says `ready` for the frame on its way out, which answers nothing, and that answer
    // would mark a list the reloaded site then sends as one that could not be loaded.
    if (
      status !== 'ready' ||
      bridgeLoad !== probeAttempt ||
      !activeId ||
      activeCustom ||
      content ||
      unreadable[activeId] ||
      awayIds.has(activeId)
    )
      return undefined;
    // A list the site has yet to report is read once it has: asked before, the site sends nothing for it. It cannot be
    // loaded only once the site has stopped reporting, on the load of the probe the tab asked for, without it.
    if (activeUnreported) {
      if (sourcesDoneNow) setUnreadable((prev) => (prev[activeId] ? prev : { ...prev, [activeId]: true }));
      return undefined;
    }
    let cancelled = false;
    void getSource(activeId).then((value) => {
      if (cancelled) return;
      const parsed = asSourceContent(value);
      if (parsed) setContents((prev) => ({ ...prev, [activeId]: parsed }));
      else setUnreadable((prev) => ({ ...prev, [activeId]: true }));
    });
    return () => {
      cancelled = true;
    };
  }, [
    status,
    bridgeLoad,
    probeAttempt,
    activeId,
    activeCustom,
    content,
    unreadable,
    getSource,
    awayIds,
    activeUnreported,
    sourcesDoneNow,
  ]);


  /**
   * The list's own order, before any unsaved change.
   *
   * For one of the OWNER's lists this is the stored id list itself, not the ids of the games that rendered.
   * That distinction is the whole reason this area kept losing games: the rendered set is a lossy projection
   * of the stored one (it drops every id the catalogue no longer holds), and every write was built from the
   * projection. Reading the stored ids here means a write can only ever be a permutation of them.
   *
   * A SITE list has no such stored value here: its order IS what the site served, so it is read from the
   * content as before.
   */
  // An automatic list's order is over the games its filters match, so its own order is those, as they resolve.
  const originalIds = React.useMemo(
    () =>
      activeCustom
        ? activeCustom.mode === 'auto'
          ? activeCustomGames.map(gameKey)
          : activeCustom.gameIds
        : content
          ? gamesOf(content).map(gameKey)
          : [],
    [activeCustom, activeCustomGames, content],
  );
  const draft = activeId ? drafts[activeId] : undefined;
  // The draft the service has for this list, when it differs from the list's own order (an autosave the
  // owner left behind and is now reloading). Used only when there is no fresher local draft this session.
  const savedDraft = source.status === 'ready' ? source.draft : null;
  const serverDraft =
    savedDraft !== null && savedDraft.length > 0 && !orderHolds(savedDraft, originalIds) ? savedDraft : undefined;
  const order = draft ?? serverDraft ?? originalIds;
  // A hand-picked list is exactly its order: a game taken out of it (Remove from list on a placeholder) leaves
  // the grid at once. Put back at the end, as a game the order does not name is for a site or an automatic
  // list (where games arrive without the owner), it would sit there until the save landed, and a move made
  // meanwhile would hand it back into the order.
  const games = React.useMemo(() => {
    if (!content) return [];
    const ordered = orderedGames(gamesOf(content), order);
    if (activeCustom?.mode !== 'manual') return ordered;
    const kept = new Set(order);
    return ordered.filter((game) => kept.has(gameKey(game)));
  }, [content, order, activeCustom]);
  // Not strict equality: games the platform adds to a published list land after the owner's, and that is
  // not a change of theirs to publish. `dropPublishedDraft` below still compares strictly, because there
  // the question is whether the draft is byte-for-byte the one that was just sent.
  // A site list whose rows have not been read on this page yet has no order of its own to differ from: its
  // saved draft against nothing would read as a change (a phantom "unpublished" mark) when nothing is.
  const dirty = (activeCustom !== undefined || content !== undefined) && !orderHolds(order, originalIds);
  // The lists with unpublished game-order changes (the dot on their chips). Until the open list's own state
  // has loaded, what the service held for it when the page opened stands in.
  const siteOrders = React.useMemo(
    () => Object.fromEntries(Object.entries(contents).map(([listId, c]) => [listId, gamesOf(c).map(gameKey)])),
    [contents],
  );
  // Which list the hook's state belongs to. Right after the open list changes, the hook still holds the
  // previous list's state (status "ready") for a render, until it reloads; its state counts only once it
  // turned ready again for the list now open.
  const [readyFor, setReadyFor] = React.useState<string | undefined>(undefined);
  const lastStatus = React.useRef(source.status);
  React.useEffect(() => {
    if (lastStatus.current === source.status) return;
    lastStatus.current = source.status;
    setReadyFor(source.status === 'ready' ? activeId : undefined);
  }, [source.status, activeId]);
  const hookReady = source.status === 'ready' && readyFor === activeId;

  // The open list's state as the hook holds it now. An empty saved draft is no draft for a game list (that is
  // what Reset saves), not a cleared order. Whether it is the whole catalogue too: its read can answer before the page's
  // read of the same list, which then keeps this one (a catalogue list first on the page is the open one at first).
  const openSnapshot = React.useMemo(
    (): OrderedSourceSnapshot | null =>
      hookReady
        ? {
            draft: source.draft && source.draft.length ? source.draft : null,
            published: source.published,
            prevPublished: source.prevPublished,
            live: source.live,
            canRevert: source.canRevert,
            allowTenantSources: source.allowTenantSources,
            ...(source.catalogue ? { catalogue: true } : {}),
          }
        : null,
    [
      hookReady,
      source.draft,
      source.published,
      source.prevPublished,
      source.live,
      source.canRevert,
      source.allowTenantSources,
      source.catalogue,
    ],
  );
  // Keeps the open list's latest state for after the owner moves on to another list.
  React.useEffect(() => {
    if (activeId && openSnapshot) {
      setSnapshots((prev) => ({
        ...prev,
        [activeId]: prev[activeId]?.catalogue ? { ...openSnapshot, catalogue: true } : openSnapshot,
      }));
    }
  }, [activeId, openSnapshot]);
  const unpublished = React.useMemo(
    () =>
      unpublishedLists({
        // Only site lists can be published, so only they carry the unpublished-changes mark.
        listIds: siteLists.map((list) => list.id),
        drafts,
        siteOrders,
        snapshots: activeId && openSnapshot ? { ...snapshots, [activeId]: openSnapshot } : snapshots,
        // The open list goes by its Publish button once its state is in (or when there is no service to
        // wait for); until then it is read like the others, so its mark does not blink on a switch.
        activeId,
        // A list the site does not draw has no games here to be dirty with, and neither has a site list whose games the
        // site has yet to send (one the chips name on the service's word waits seconds for the site to report it): it
        // is read like the others, on its saved state, so a draft it holds keeps its mark while it is open, and Publish
        // all counts it.
        activeDirty:
          activeId !== undefined && (awayIds.has(activeId) || (!activeCustom && content === undefined))
            ? undefined
            : !activePublishable || hookReady
              ? dirty
              : undefined,
      }),
    [
      siteLists,
      drafts,
      siteOrders,
      snapshots,
      activeId,
      openSnapshot,
      activePublishable,
      hookReady,
      dirty,
      awayIds,
      activeCustom,
      content,
    ],
  );
  // The info point beside the lists of a page showing the Mexican market's lists (see `showsMexicanLists`): the
  // platform's initial order of their games was modelled on the market's top sites. Open as a page is entered when
  // every site list on it still shows that order, else closed (its icon opens it). Decided once per entry, once this
  // page's own lists are in (`listsIn`, never the chips drawn on an earlier visit: the switch leaves the tab without
  // lists for a moment) and each one's state is in (a state that could not be read leaves it closed), then held for the
  // visit: a reorder made meanwhile leaves it as it is, the owner may be reading it. The owner's own lists have no
  // initial order of the platform's: they do not count, and no info point is offered while one is open. The note opens
  // inside the lists' box, beside the icon, which turns into an X while it is: closed with that X, every page is
  // entered with it closed, on later visits too, until the icon opens it again (see `initial-order-note`). A list the
  // page reports only after the decision (a category page keeps reporting) is counted once its state is in: one not in
  // the initial order closes a note the rule opened, never one the owner opened, and as it is counted as it arrives, a
  // reorder of theirs never counts.
  const [infoPoint, setInfoPoint] = React.useState<InfoPointState>(() => enteredInfoPoint(page));
  // The info point is the site's: offered on every page of a site any page of which shows the Mexican lists.
  const siteShowsMexicanLists = React.useMemo(() => pages.some(showsMexicanLists), [pages]);
  const [noteClosed, setNoteClosed] = React.useState(readInitialOrderNoteClosed);
  // Entered: decided anew, on this entry's reads. A list whose state could not be read before is read again as the
  // page's lists come in (see the reads above); until then it is not known, rather than still unreadable.
  if (infoPoint.page !== page) {
    setInfoPoint(enteredInfoPoint(page));
    if (statesUnread.size > 0) setStatesUnread(new Set());
  }
  // Decided on every list of the page but the whole catalogue, a list whose state is still awaited included (it is
  // 'pending' until then): one the chips draw only once its state is in still counts for the decision.
  const decidedLists = reportedLists.filter((list) => !snapshots[list.id]?.catalogue);
  if (infoPoint.page === page && listsIn && decidedLists.length > 0) {
    const verdictOf = (listIds: string[]): InitialOrderVerdict =>
      initialOrderVerdict({
        listIds,
        drafts,
        siteOrders,
        snapshots: activeId && openSnapshot ? { ...snapshots, [activeId]: openSnapshot } : snapshots,
        unread: statesUnread,
      });
    const listIds = decidedLists.map((list) => list.id);
    if (infoPoint.open === null) {
      const initialOrder = verdictOf(listIds);
      if (initialOrder !== 'pending') {
        setInfoPoint({ ...infoPoint, open: !noteClosed && initialOrder === 'original', counted: new Set(listIds) });
      }
    } else {
      const arrived = listIds.filter((id) => !infoPoint.counted.has(id) && verdictOf([id]) !== 'pending');
      if (arrived.length > 0) {
        const leftInitial = arrived.some((id) => verdictOf([id]) !== 'original');
        setInfoPoint({
          ...infoPoint,
          open: infoPoint.open && !(infoPoint.byRule && leftInitial),
          counted: new Set([...infoPoint.counted, ...arrived]),
        });
      }
    }
  }
  const infoPointShown =
    siteShowsMexicanLists &&
    infoPoint.page === page &&
    infoPoint.open !== null &&
    siteLists.length > 0 &&
    activeId !== undefined &&
    !activeCustom;
  const infoPointNoteId = React.useId();
  // The note and the icon as drawn: they follow the decision two frames later, so a note decided open as the page is
  // entered is first drawn shut and then folds open, as it does when the icon opens it.
  const noteOpen = infoPointShown && infoPoint.open === true;
  const [noteDrawnOpen, setNoteDrawnOpen] = React.useState(false);
  React.useEffect(() => {
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => setNoteDrawnOpen(noteOpen));
    });
    return () => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [noteOpen]);
  // The icon opens the note, and so ends a close made with the X; the X it turns into closes it on every page.
  const toggleInfoPoint = (): void => {
    const opening = !infoPoint.open;
    writeInitialOrderNoteClosed(!opening);
    setNoteClosed(!opening);
    setInfoPoint((prev) => ({ ...prev, open: opening, byRule: false }));
  };

  // The drafts the preview overlay renders: the local map, plus the reloaded server draft for the active
  // list when the owner has no local draft for it (so the preview matches what the tab shows). Memoised on
  // its inputs because GameListPreview keys its site-bridge + pin effects on this object's identity.
  // The site lists' drafts only: the owner's own lists reach the preview by their source ids (`customPreview`),
  // and a draft keyed by a list's own id is a list no page reports, which the preview would wait for in vain.
  const previewDrafts = React.useMemo(() => {
    const siteDrafts = Object.fromEntries(Object.entries(drafts).filter(([id]) => !customById.has(id)));
    return activeId && !drafts[activeId] && serverDraft ? { ...siteDrafts, [activeId]: serverDraft } : siteDrafts;
  }, [activeId, drafts, serverDraft, customById]);

  // What each page is called: the words the site's own navigation shows for it, else a name read from its
  // path. The navigation is read once per source as the pages load, and kept in the order it was read, which
  // is the order its words are preferred in (see `navigationSourceIds`). A source once read is never read again, so
  // a later read that answers nothing (a timeout, the frame reloading) cannot undo it; one whose read answered nothing
  // is asked again at the next report, and one whose content holds no rows is read all the same, linking nothing.
  const navReadRef = React.useRef(new Set<string>());
  const categorySources = React.useMemo(
    () => new Set(pages.filter((p) => p.kind === 'category').map((p) => p.source_id)),
    [pages],
  );
  React.useEffect(() => {
    // Only a page other than the home page is named by the navigation.
    if (status !== 'ready' || pages.every((p) => p.path === HOME_PAGE)) return;
    // The sources being read or read: each is asked once, and again only after a read that answered nothing.
    const asked = navReadRef.current;
    const askedOf = frameSrc;
    for (const id of navigationSourceIds(sources, categorySources).filter((source) => !asked.has(source))) {
      asked.add(id);
      void getSource(id).then((value) => {
        if (!isAlive()) return;
        if (value === null || value === undefined) {
          asked.delete(id);
          if (frameNow.current.src !== askedOf || !frameNow.current.ready) return;
          setNavRead((prev) => (prev.unread.has(id) ? prev : { ...prev, unread: new Set(prev.unread).add(id) }));
          return;
        }
        const content = asSourceContent(value);
        if (content) setNavContents((prev) => [...prev, content]);
        setNavRead((prev) => ({
          read: new Set(prev.read).add(id),
          unread: prev.unread.has(id) ? new Set([...prev.unread].filter((other) => other !== id)) : prev.unread,
        }));
      });
    }
  }, [status, sources, pages, categorySources, getSource, frameSrc, isAlive]);
  // Once the page's own sources are in (`navSourcesIn`) and the navigation this page reported has all been read, the
  // paths it links to join the linked ones.
  React.useEffect(() => {
    if (status !== 'ready' || !navSourcesIn) return;
    const wanted = navigationSourceIds(sources, categorySources);
    if (wanted.length === 0 || !wanted.every((id) => navRead.read.has(id))) return;
    const now = linkedPaths(navContents);
    setLinked((prev) => {
      if (prev.known && [...now].every((path) => prev.paths.has(path))) return prev;
      return { paths: new Set([...prev.paths, ...now]), known: true };
    });
  }, [status, navSourcesIn, sources, categorySources, navRead, navContents]);
  // Whether the tree is drawn yet (see `visiblePages`). Until the pages are decided, every page the service names is
  // offered, and those the site's navigation does not link (often named in English, from the service) showed for a
  // moment and went. So the tree holds the pages that stay whatever is decided (the home page and the one on screen)
  // and placeholders for the rest until then (by the service's word, as soon as it and the owner's lists are in, see
  // `pagesDecided`); or until there is nothing to decide, no page besides the home page or none the navigation could
  // hide (`frameCanHide`), which then need not wait for the site to boot in the frame; or until the decision cannot
  // come: the site not framed, a site that reports no navigation, a navigation read that answered nothing, the owner's
  // lists unreadable, or PAGES_DECIDE_WAIT_MS gone by. "Reports no navigation" and "answered nothing" are taken only
  // once the site is done reporting (`sourcesDone`): the bridge still asks after the page's own sources are in, for the
  // lists the service names (`awaitedLists`), and a navigation reported after them would otherwise be taken for none,
  // the tree drawn undecided for the visit. The navigation is read as soon as the page's own sources are in
  // (`navSourcesIn`), so a list the site never reports does not hold the decision back.
  // Once drawn in full the tree stays so for the visit, and a page it drew stays with it (`drawnPages`), whatever is
  // decided after.
  // Before the service has named the site's pages there is nothing to decide either, the home page alone: while the
  // site's host is still looked up (the Creatives page opens the tab before it is found), while the service reads them,
  // or once the read failed (until Try again). That is no answer of the service's: it draws the tree as it stands (the
  // home page alone, the placeholders coming with the pages it names), but only the service's answer settles the tree
  // for good, and the wait runs only from that answer. Taken for one, and kept, it drew the pages named afterwards
  // undecided the moment they came in.
  const sitePagesNamed = sitePagesRead === 'ok';
  const frameCanHide = React.useMemo(
    () => offeredSitePages(pages, keptWithoutFrame).length < pages.length,
    [pages, keptWithoutFrame],
  );
  const nothingToDecide = !frameCanHide;
  const [pagesWaited, setPagesWaited] = React.useState(false);
  const pagesSettleNow =
    pagesDecided ||
    (sitePagesNamed && nothingToDecide) ||
    status === 'failed' ||
    (sourcesDone && navigationSourceIds(sources, categorySources).length === 0) ||
    (sourcesDone && navRead.unread.size > 0) ||
    (ownListsOn && ownListsAnswered && !ownListsLoaded);
  React.useEffect(() => {
    if (pagesWaited) return undefined;
    // Latched: a later navigation read that answers nothing must not take the drawn row back to placeholders.
    if (pagesSettleNow) {
      setPagesWaited(true);
      return undefined;
    }
    // The wait runs from the service's answer, not from the visit: an answer slower than the wait (14 s measured on
    // production) would otherwise come in after it and be drawn undecided.
    if (!sitePagesNamed) return undefined;
    const timer = window.setTimeout(() => setPagesWaited(true), PAGES_DECIDE_WAIT_MS);
    return () => window.clearTimeout(timer);
  }, [pagesSettleNow, pagesWaited, sitePagesNamed]);
  // The site's pages are named in the site's own words, its translation table's for its language (`siteWords`): a
  // Mexican site's navigation says "Crash Games", and the site shows "Juegos Crash". Named before that table was in, a
  // page read in the sources' words, often English, and was renamed a moment later. So the tree names the site's pages
  // once the table is in, or its read failed (a site without one is not waited for), and at the latest once
  // SITE_WORDS_WAIT_MS has gone by, when they are named as the sources give them; it holds placeholders for them until
  // then. No wait for the home page alone (the hub's own word for it), for a site that names no language (no table to
  // read), nor once the tree has named the site's pages: a drawn name never goes back to a placeholder.
  const namesHeld = !siteWordsIn && pages.length > 1 && ![...drawnPages].some((path) => path !== HOME_PAGE);
  const pageTabsSettled = !namesHeld && (pagesWaited || pagesSettleNow || nothingToDecide);
  // The pages the drawn tree offers join `drawnPages` in the very render that draws them, and a decision is recorded in
  // the render that makes it: set while rendering, as `listsShownOn` is, so no decision that comes after can render the
  // tree without them, and no read that answers nothing after can open it to every page.
  if (pagesDecided && !decidedDrawn) setDecidedDrawn(true);
  if (pageTabsSettled && visiblePages.some((p) => !drawnPages.has(p.path))) {
    setDrawnPages(new Set([...drawnPages, ...visiblePages.map((p) => p.path)]));
  }
  // A page's name: the service's words for it (the site's own navigation text) in the site's language, else
  // English, else what the site's navigation calls it (`navWord`: the service's word for that, else the navigation read
  // from the framed site), each as the site shows it (`siteWords`), else a name read from its path. A list page its
  // page's menu does not link is reached through that navigation alone, and named as it names it: the service may name
  // it from a hidden page's menu (playavenue's footer links /live-casino/all-live-casino as "Casino Vivo", which the
  // service calls "Todos los Juegos", the name of Casino en Vivo's own menu list beside it). The home page is the hub's
  // own word for it.
  const pageLabel = React.useCallback(
    (path: GamePage): string => {
      if (path === HOME_PAGE) return t('Homepage');
      const sitePage = pages.find((p) => p.path === path);
      const navText = navWord(sitePage, path);
      if (sitePage?.kind === 'list' && sitePage.inMenu === false && navText) return titleCaseShouting(navText);
      const named = labelIn(sitePage?.label, textLanguage);
      return titleCaseShouting((named !== null ? siteWords(named) : null) ?? navText ?? pathLabel(path));
    },
    [t, pages, textLanguage, navWord, siteWords],
  );
  // The site's pages as the page tree draws them: every top page, each with its single-list pages under it, once the
  // pages are decided; until then the home page and the one the owner is on, alone, with placeholders after them.
  const treeNodes: PageTreeNode[] = React.useMemo(
    () =>
      (pageTabsSettled
        ? pageGroups
        : pageGroups.filter(({ page: { path } }) => path === HOME_PAGE || path === pageGroup.page.path)
      ).map(({ page: { path }, lists: under }) => ({
        path,
        label: pageLabel(path),
        children: pageTabsSettled ? under.map((p) => ({ path: p.path, label: pageLabel(p.path) })) : [],
      })),
    [pageTabsSettled, pageGroups, pageGroup.page.path, pageLabel],
  );

  // A draft changed or dropped from anywhere (this tab, or a list reordered in the site preview). Mirrors
  // the change into the hook (which debounce-autosaves it) when it is the ACTIVE list; a null drops it,
  // which the hook records as an empty draft (its own order). Other lists stay local-map only.
  const handleDraftChange = React.useCallback(
    (dsId: string, ids: string[] | null): void => {
      setDrafts((prev) => {
        const next = { ...prev };
        if (ids) next[dsId] = ids;
        else delete next[dsId];
        return next;
      });
      if (dsId === activeId) setSourceDraft(ids ?? []);
    },
    [activeId, setSourceDraft],
  );

  // FTUX: whether the games drag under way has moved a game (null while none is). A drag reorders the grid under the
  // pointer at every step, so its move is told once, at the drop (handleDraggingChange).
  const dragMovedRef = React.useRef<boolean | null>(null);
  const handleReorder = React.useCallback(
    (ids: string[]): void => {
      if (!activeId) return;
      if (dragMovedRef.current === null) ftuxSignal('games-moved');
      else dragMovedRef.current = true;
      // The grid can only hand back a permutation of what it was SHOWN. For one of the owner's lists that
      // is fewer ids than the list holds whenever the catalogue is missing one of its games, so the new
      // order is merged INTO the stored order rather than replacing it. Replacing it is what lost games: a
      // draft built from the screen alone had already dropped everything the screen could not draw.
      handleDraftChange(activeId, activeCustom ? mergeShownOrder(order, ids) : ids);
    },
    [activeId, activeCustom, order, handleDraftChange],
  );

  // "Remove from list" on a game the catalogue no longer holds: a change to the open hand-picked list's order
  // like any other, stored as its draft by the effect that stores reorders. Never its last game: the service
  // refuses a hand-picked list with none, and emptying a list is deleting it (as in its editor).
  const removable = activeCustom?.mode === 'manual' && order.length > 1;
  const handleRemoveGame = React.useCallback(
    (id: string): void => {
      if (!activeId || activeCustom?.mode !== 'manual' || order.length <= 1) return;
      handleDraftChange(activeId, order.filter((gameId) => gameId !== id));
    },
    [activeId, activeCustom, order, handleDraftChange],
  );

  // Drop only the LOCAL draft for a list (no autosave PUT), after a publish/revert has already settled the
  // server side, so it does not fire an extra empty-draft write the way handleDraftChange(null) would.
  /** Drops a list's draft after a publish, but only while it is still the order that was published. The
   *  grid stays live during a Publish all run, so a reorder made mid-flight is NEWER than what landed and
   *  has to survive it rather than be cleared as though it had been published. */
  const dropPublishedDraft = React.useCallback((listId: string, published: string[]): void => {
    setDrafts((prev) => {
      const mine = prev[listId];
      if (!mine || !sameOrder(mine, published)) return prev;
      const next = { ...prev };
      delete next[listId];
      return next;
    });
  }, []);

  const dropLocalDraft = React.useCallback((listId: string): void => {
    setDrafts((prev) => {
      if (!prev[listId]) return prev;
      const next = { ...prev };
      delete next[listId];
      return next;
    });
  }, []);

  const handleContent = React.useCallback((dsId: string, value: SiteSourceContent): void => {
    setContents((prev) => (prev[dsId] ? prev : { ...prev, [dsId]: value }));
  }, []);

  const handleReset = (): void => {
    if (activeId) handleDraftChange(activeId, null);
  };

  // A refusal is the service's own reason and shown as is; anything else gets the generic line.
  // The one place a failed write becomes copy. A useCallback so the list handlers below can depend on it
  // without being rebuilt every render.
  const notifyFailure = React.useCallback(
    (result: TenantSourceFailure, generic: string, unavailable?: string): void => {
      // `unavailable` is the route not being deployed in this environment, which is a different thing to
      // say than a write that was attempted and failed, and it differs per action: telling someone their
      // list could not be SAVED because PUBLISHING is unavailable explains nothing.
      if (result.reason === 'unavailable') {
        onNotify?.(unavailable ?? t('Publishing game lists is not available yet.'));
      } else onNotify?.(result.reason === 'refused' && result.message ? result.message : generic);
    },
    [onNotify, t],
  );

  // One of the owner's own lists can be unpublished in two ways, and Publish changes publishes both. Its
  // GAMES: a reorder or an edit is stored as a draft, and the site keeps showing the published games until the
  // owner publishes, as for every list here. Its PLACE on the page (where it sits, hidden or not, its title),
  // which reaches the site through the page's arrangement.
  const workingIdsOf = React.useCallback(
    // A reorder still being stored, else what is stored.
    (custom: CustomList): string[] => drafts[custom.id] ?? custom.gameIds,
    [drafts],
  );
  // Its games as the owner has them: an automatic list's matches in its working order.
  const workingMembersOf = React.useCallback(
    (custom: CustomList): string[] => ownListMembers(workingStateOf(custom, workingIdsOf(custom)), catalog),
    [workingIdsOf, catalog],
  );
  // Without the catalogue (its read failed) an automatic list is compared by its saved order (see `ownListChanged`):
  // resolving nothing on either side read as no change.
  const gamesUnpublished = React.useCallback(
    (custom: CustomList): boolean =>
      ownListChanged(
        workingStateOf(custom, workingIdsOf(custom)),
        publishedStateOf(custom),
        catalogFailed ? null : catalog,
      ),
    [workingIdsOf, catalog, catalogFailed],
  );
  const placementChanged = React.useCallback(
    (custom: CustomList): boolean => workingPage !== null && listChanged(livePage, workingPage, custom.sourceId),
    [workingPage, livePage],
  );
  // The owner's lists on this page with anything unpublished (the dot on their chips)...
  const customUnpublished = React.useMemo(
    () => new Set(pageCustom.filter((c) => gamesUnpublished(c) || placementChanged(c)).map((c) => c.id)),
    [pageCustom, gamesUnpublished, placementChanged],
  );
  // ...and the ones whose GAMES wait: each is a change of its own in the bar, as a site list is. Their place
  // is counted with the rest of the page's arrangement.
  const customGamesWaiting = React.useMemo(() => pageCustom.filter(gamesUnpublished), [pageCustom, gamesUnpublished]);
  const activeGamesUnpublished = activeCustom ? gamesUnpublished(activeCustom) : false;
  const hasUnpublished = (chipId: string): boolean => unpublished.has(chipId) || customUnpublished.has(chipId);

  // The preview pins the owner's lists into the framed site as whole lists, which skips the site's own hydration of
  // them: they are drawn from the FULL catalogue (art and all), not the slim one the tab holds, which would draw blank
  // tiles. It is read only while the preview is open on a page with lists of the owner's, and the frame waits for it
  // (undefined only while it is read), whether or not one of those lists has games waiting: a draft handed over once the
  // framed site has reported its lists is never pinned, and a list opened in the preview's sheet before it is handed over
  // has only what the site serves to show (see `handlePreviewDraftChange`). A read that fails hands none of them over,
  // and the site shows them as it serves them.
  const previewCatalog = useFullCatalogue(previewOpen && pageCustom.length > 0);
  // Held only until the frame is first shown: a list that comes to wait after that (a reorder in the preview's own
  // sheet) is pinned as that sheet closes, and the frame is never taken back from the owner.
  const [previewShown, setPreviewShown] = React.useState(false);
  if (!previewOpen && previewShown) setPreviewShown(false);
  const previewHeld = previewOpen && !previewShown && previewCatalog === undefined;
  if (previewOpen && !previewShown && !previewHeld) setPreviewShown(true);

  // The preview is handed the owner's lists whose games are a draft as the tab has them (see
  // `ownListsInPreview`), once the catalogues that draw them are in. Only while it is open: resolving them walks
  // the whole catalogue, and the drafts change on every step of a drag.
  // Every own list on the page gets its games (a list just published from the preview's sheet included), and
  // the ones with games waiting their draft.
  const customPreview = React.useMemo(
    () =>
      ownListsInPreview(
        previewOpen && catalogUsable && previewCatalog
          ? pageCustom.map((custom) => ({ sourceId: custom.sourceId, ids: workingMembersOf(custom) }))
          : [],
        previewCatalog ?? NO_GAMES,
        new Set(customGamesWaiting.map((custom) => custom.sourceId)),
      ),
    [previewOpen, catalogUsable, previewCatalog, pageCustom, customGamesWaiting, workingMembersOf],
  );
  // What the preview is handed: the site lists' drafts, and the owner's lists as above. Memoised because the
  // preview keys its pins on these objects' identity.
  const previewAllDrafts = React.useMemo(
    () => ({ ...previewDrafts, ...customPreview.drafts }),
    [previewDrafts, customPreview],
  );
  const previewContents = React.useMemo(() => ({ ...contents, ...customPreview.contents }), [contents, customPreview]);
  // The preview names one of the owner's lists by its source id; the tab, by the list's own id.
  const handlePreviewDraftChange = React.useCallback(
    (dsId: string, ids: string[] | null): void => {
      const custom = customBySource.get(dsId);
      if (!custom) {
        handleDraftChange(dsId, ids);
        return;
      }
      // A list the preview was not handed (a catalogue it is drawn from could not be read, or is not in yet) is shown
      // there as the site serves it, its PUBLISHED games: merged into the working order, a reorder of those would replace
      // the owner's unpublished one with them. Checked on what was handed over, not on the preview's contents, which then
      // hold the site's own copy of the list, read by the sheet.
      if (!customPreview.contents[dsId]) return;
      // The sheet can only hand back what it was shown, so the order is merged into the working one, as a
      // reorder in the tab is: an id the catalogue cannot draw keeps its place instead of being dropped.
      handleDraftChange(custom.id, ids ? mergeShownOrder(workingMembersOf(custom), ids) : null);
    },
    [customBySource, customPreview, handleDraftChange, workingMembersOf],
  );

  /** Puts one of the owner's lists' games on the site, as given: stored, published and recorded. */
  const publishCustomGames = React.useCallback(
    async (custom: CustomList, ids: string[]): Promise<Awaited<ReturnType<typeof saveOwnerGameList>>> => {
      if (!casinoInstanceId) return { ok: false, reason: 'unavailable' };
      setWritingList(true);
      const result = await saveOwnerGameList(casinoInstanceId, {
        id: custom.id,
        page,
        name: custom.name,
        game_ids: ids,
        kind: custom.kind,
        layout: custom.layout,
        mode: custom.mode,
        filters: custom.filters,
        publish: true,
      });
      if (!isAlive()) return result;
      setWritingList(false);
      if (result.ok) {
        setCustomLists((prev) =>
          withOwnListChanged(prev, custom.id, (l) => ({
            ...l,
            gameIds: ids,
            publishedIds: ids,
            publishedMode: custom.mode,
            publishedFilters: custom.filters,
          })),
        );
        listWritesRef.current += 1;
        setFailedOrder((prev) => (prev.startsWith(`${custom.id}:`) ? '' : prev));
        dropPublishedDraft(custom.id, ids);
      }
      return result;
    },
    [casinoInstanceId, page, isAlive, dropPublishedDraft],
  );

  /** Revert to initial state, for one of the owner's own: its games back to what the site shows, as a draft
   *  save (nothing on the site changes, since it already shows them). */
  const revertCustomList = async (custom: CustomList): Promise<void> => {
    if (!casinoInstanceId || writingList) return;
    setWritingList(true);
    const result = await saveOwnerGameList(casinoInstanceId, {
      id: custom.id,
      page,
      name: custom.name,
      game_ids: custom.publishedIds,
      kind: custom.kind,
      layout: custom.layout,
      // Back to how the site builds it too: an automatic list's filters, or hand-picked again.
      mode: custom.publishedMode,
      filters: custom.publishedFilters,
    });
    if (!isAlive()) return;
    setWritingList(false);
    if (!result.ok) {
      notifyFailure(result, t('That list could not be saved.'), t('Saving your own game lists is not available yet.'));
      return;
    }
    setCustomLists((prev) =>
      withOwnListChanged(prev, custom.id, (l) => ({
        ...l,
        gameIds: custom.publishedIds,
        mode: custom.publishedMode,
        filters: custom.publishedFilters,
      })),
    );
    listWritesRef.current += 1;
    setFailedOrder((prev) => (prev.startsWith(`${custom.id}:`) ? '' : prev));
    dropLocalDraft(custom.id);
  };

  /**
   * Publish every list on this page that has unpublished changes. The open list goes through its own hook
   * so its state stays in step; the rest are sent straight to the service, since they are not loaded here.
   */
  // What is waiting on this page: the lists whose games have been edited but not published (the site's and
  // the owner's own alike), and the page's own arrangement (which lists it shows, in what order) when that
  // differs from what is live. The arrangement is ONE change however much of it moved.
  const edited = unpublished.size + customGamesWaiting.length;
  const waiting = edited + (pageChanged ? 1 : 0);

  const barNow: ChangeBarState = {
    show: canPublish && waiting > 0,
    sendable: waiting,
  };
  const barNowRef = React.useRef(barNow);
  barNowRef.current = barNow;
  // A drag holds the bar exactly as it stood when the drag began, and releases it on the drop. Both halves
  // matter: it would otherwise fade in and out under the owner's hands mid-drag, and a desktop reorder is
  // realtime, so a tile dragged back to its own slot would otherwise leave it up reading "Publish all
  // changes (0)" until the drop.
  const bar = frozenBar ?? barNow;
  const publishAllShown = bar.show;
  // Publish changes as it last stood while shown. It keeps that count and look while it fades out, rather
  // than reading "(0)" or lighting back up as the publish that emptied it ends.
  // Held while a write runs, a list's own (a rename, a reorder being stored) too: Publish all waits for it.
  const shownPublishAll = React.useRef({ count: bar.sendable, disabled: writing !== null || writingList });
  if (publishAllShown) shownPublishAll.current = { count: bar.sendable, disabled: writing !== null || writingList };

  /** Stores one of the owner's lists in a given order, as a DRAFT: the site keeps showing the published
   *  games until the owner publishes. Stored rather than kept in the tab, so a reorder survives a reload,
   *  as a site list's draft does. */
  const persistCustomOrder = React.useCallback(
    async (list: CustomList, ids: string[]): Promise<void> => {
      if (!casinoInstanceId) return;
      setWritingList(true);
      const result = await saveOwnerGameList(casinoInstanceId, {
        id: list.id,
        page,
        name: list.name,
        game_ids: ids,
        kind: list.kind,
        layout: list.layout,
        mode: list.mode,
        filters: list.filters,
      });
      if (!isAlive()) return;
      setWritingList(false);

      if (!result.ok) {
        // Remembered so the effect does not immediately try the same order again: its input, the draft, is
        // unchanged by the failure. A different order clears it by simply not matching, and so does Retry.
        setFailedOrder(`${list.id}:${ids.join()}`);
        notifyFailure(
          result,
          t('That list could not be saved.'),
          t('Saving your own game lists is not available yet.'),
        );
        return;
      }
      // Only this list's: another list's refused order stays remembered, or it would be sent again.
      setFailedOrder((prev) => (prev.startsWith(`${list.id}:`) ? '' : prev));
      setCustomLists((prev) => withOwnListChanged(prev, list.id, (l) => ({ ...l, gameIds: ids })));
      listWritesRef.current += 1;
      // The draft is dropped ONLY while it is still the order that was just stored. A reorder made during
      // the round trip is newer than what landed, so clearing it unconditionally both discarded the owner's
      // change and snapped the grid back to the order before it.
      setDrafts((prev) => {
        const mine = prev[list.id];
        if (!mine || !sameOrder(mine, ids)) return prev;
        const next = { ...prev };
        delete next[list.id];
        return next;
      });
    },
    [casinoInstanceId, page, notifyFailure, t, isAlive],
  );

  /**
   * Stores a reorder of one of the owner's own lists.
   *
   * Keyed off the DRAFT rather than off a drag, because a drag is only one of three ways to reorder: a
   * phone reorders by press-and-hold through `GameListRows`, and every tile carries a Move menu. Both go
   * straight to `onReorder` and never touch the drag state, so a drag-end hook silently persisted nothing
   * on a phone, where a game is reordered by press and hold.
   *
   * It waits for the drag to END so a realtime desktop reorder is one write rather than one per pointer
   * step, and it requires a draft to exist, which is what distinguishes a reorder the owner performed from
   * the grid merely mounting.
   *
   * The draft it stores is already a full permutation of the stored ids (see `handleReorder`), so nothing is
   * added to it here. It also re-runs when a write finishes, which is how a reorder made during the previous
   * round trip gets stored rather than dropped.
   */
  React.useEffect(() => {
    // Held while a publish runs too: Publish all publishes each own list as it stood at the click, and would put
    // that older order back over this one. The draft survives the publish and is stored after it.
    if (!activeCustom || dragging || writingList || writing !== null) return;
    const draftOrder = drafts[activeCustom.id];
    if (!draftOrder) return;
    if (sameOrder(draftOrder, activeCustom.gameIds)) return;
    if (failedOrder === `${activeCustom.id}:${draftOrder.join()}`) return;
    void persistCustomOrder(activeCustom, draftOrder);
  }, [activeCustom, dragging, writingList, writing, drafts, failedOrder, persistCustomOrder]);
  // The open list's order is on screen but its write failed. The change bar leaves the owner's own lists
  // out by design, so without this nothing said the order shown was not the order stored.
  const activeOrderUnsaved =
    Boolean(activeCustom) && failedOrder !== '' && failedOrder === `${activeCustom?.id}:${order.join()}`;

  // Two things drag on this tab, the open list's games and the chips, and the bar holds while EITHER does. Each
  // reports for itself: the game grid reports "not dragging" whenever it mounts or unmounts (the open list's
  // games arriving, say), which released the bar in the middle of a chip drag when the two shared one flag.
  const dragSources = React.useRef(new Set<'games' | 'chips'>());
  const setDragSource = React.useCallback((source: 'games' | 'chips', isDragging: boolean): void => {
    const was = dragSources.current.size > 0;
    if (isDragging) dragSources.current.add(source);
    else dragSources.current.delete(source);
    const now = dragSources.current.size > 0;
    if (was === now) return;
    setFrozenBar(now ? barNowRef.current : null);
    setDragging(now);
  }, []);
  const handleDraggingChange = React.useCallback(
    (isDragging: boolean): void => {
      setDragSource('games', isDragging);
      // FTUX: a drag that moved a game is that game's move, told as it drops.
      const moved = dragMovedRef.current === true;
      dragMovedRef.current = isDragging ? false : null;
      if (moved && !isDragging) ftuxSignal('games-moved');
    },
    [setDragSource],
  );
  // Publish changes must not fade in under a row being moved: it holds as it was until the drop, as it does for a game
  // drag. The sorting hook says which row is up.
  const liftedRow = chipIds.find((_, index) => chipRows.row(index).dragged) ?? null;
  React.useEffect(() => {
    if (liftedRow === null) return undefined;
    setDragSource('chips', true);
    return () => setDragSource('chips', false);
  }, [liftedRow, setDragSource]);

  // Says whether everything waiting was published.
  const handlePublishAll = async (): Promise<boolean> => {
    if (!canPublish || !casinoInstanceId || writing || writingList || waiting === 0) return false;
    const ids = [...unpublished];
    // Taken now, so what is published is the page (and each list) as it stood when Publish was pressed.
    const customToSend = customGamesWaiting.map((custom) => ({ custom, games: workingIdsOf(custom) }));
    const pageToSend = pageChanged ? workingPage : null;
    setWriting('publish');
    let done = 0;
    let failed = 0;
    // What the service said for a list it would not publish: said as is when that list was the one change to publish,
    // as the page's own refusal is (the reason a list cannot be published is the owner's to read).
    let listFailure: TenantSourceFailure | null = null;
    for (const listId of ids) {
      const send = publishOrderFor({
        listId,
        activeId,
        activeOrder: order,
        activeDirty: dirty,
        drafts,
        snapshots,
      });
      if (send === null) {
        // The bar counted this list but there is nothing to send for it. Report it rather than letting
        // the success toast claim a list that was never published.
        failed += 1;
        continue;
      }
      if (listId === activeId) {
        // Through the hook even when the list's content has not loaded, so its state moves with the
        // publish instead of going stale behind it and re-marking the list as unpublished.
        const r = await source.publish(send);
        if (!isAlive()) return false;
        if (r.ok) {
          if (contents[listId]) {
            setContents((prev) => (prev[listId] ? { ...prev, [listId]: pinnedContent(prev[listId], send) } : prev));
          } else {
            // Nothing was ever loaded for this list, so there is no baseline to pin the published order
            // against and it would keep reading as changed: a dot and a count that no retry can clear.
            // Ask the site for it again, from a reloaded frame, the way the retry button does.
            setUnreadable((prev) => {
              if (!prev[listId]) return prev;
              const next = { ...prev };
              delete next[listId];
              return next;
            });
            setProbeAttempt((n) => n + 1);
          }
          // Only a publish that landed clears the draft: a failed one leaves it to retry.
          dropPublishedDraft(listId, send);
          done += 1;
        } else {
          failed += 1;
          listFailure = r;
        }
      } else {
        const r = await publishTenantSource(casinoInstanceId, {
          kind: 'game_list',
          source_id: listId,
          ids: send,
        });
        if (!isAlive()) return false;
        if (r.ok) {
          // Its saved draft is live now. The service's own answer decides what counts as published, so a
          // later revert goes back to the right previous order. Its draft is cleared: it is published.
          setSnapshots((prev) => {
            const was = prev[listId];
            return was ? { ...prev, [listId]: { ...afterPublish(was, r.value), draft: null } } : prev;
          });
          // Pin the order we just published, or switching to this list would show its pre-publish order
          // as the clean baseline and push it back to the site on the next publish.
          setContents((prev) => (prev[listId] ? { ...prev, [listId]: pinnedContent(prev[listId], send) } : prev));
          dropPublishedDraft(listId, send);
          done += 1;
        } else {
          failed += 1;
          listFailure = r;
        }
      }
    }
    // The owner's own lists whose games wait, each with its games as they stood.
    for (const { custom, games } of customToSend) {
      const r = await publishCustomGames(custom, games);
      if (!isAlive()) return false;
      if (r.ok) done += 1;
      else {
        failed += 1;
        listFailure = r;
      }
    }
    // The page's arrangement goes last: a list it newly shows is then published with its games in order.
    let pageFailure: TenantSourceFailure | null = null;
    if (pageToSend) {
      const r = await publishArrangement(pageToSend);
      if (!isAlive()) return false;
      if (r.ok) done += 1;
      else {
        failed += 1;
        pageFailure = r;
      }
    }
    if (!isAlive()) return false;
    setWriting(null);
    const lists = ids.length + customToSend.length;
    const total = lists + (pageToSend ? 1 : 0);
    if (failed > 0) {
      if (pageFailure && total === 1) {
        notifyFailure(pageFailure, t("We couldn't publish the lists on this page. Try again."));
      } else if (listFailure && total === 1) {
        notifyFailure(listFailure, t("We couldn't publish this list. Try again."));
      } else if (pageToSend) {
        onNotify?.(
          done > 0
            ? t('Published {{done}} of {{total}} changes. Try the rest again.', { done, total })
            : t("We couldn't publish these changes. Try again."),
        );
      } else {
        onNotify?.(
          done > 0
            ? t('Published {{done}} of {{total}} lists. Try the rest again.', { done, total: lists })
            : t("We couldn't publish these lists. Try again."),
        );
      }
      return false;
    }
    ftuxSignal('games-published');
    if (pageToSend) {
      onNotify?.(t('Published. Your site now shows your changes.'));
      return true;
    }
    onNotify?.(
      done === 1
        ? t('Published 1 list. Your site now shows it.')
        : t('Published {{n}} lists. Your site now shows them.', { n: done }),
    );
    return true;
  };

  const handleRevert = async (): Promise<void> => {
    if (!activePublishable || !casinoInstanceId || !activeId || writing) return;
    const listId = activeId;
    setWriting('revert');
    const r = await source.revert();
    if (!isAlive()) return; // brand switched mid-write: this instance is gone
    setWriting(null);
    if (!r.ok) {
      notifyFailure(r, t("We couldn't undo the publish. Try again."));
      return;
    }
    // The revert restored an order (the previously published one, or the list's own): show it when the
    // service read it back AND we have the list loaded to pin it onto, else drop the content and re-read the
    // list from the site (the revert is done either way).
    const hadContent = Boolean(contents[listId]);
    setContents((prev) => {
      const next = { ...prev };
      if (r.value.live.length && next[listId]) next[listId] = pinnedContent(next[listId], r.value.live);
      else delete next[listId];
      return next;
    });
    // Whenever the content will be re-read (service gave no order, or this list wasn't loaded yet), reload
    // the probe first: its frame still serves the order that was just published (it was never reloaded), so
    // a re-read against it would show the reverted-away order, not the restored one.
    if (!r.value.live.length || !hadContent) setProbeAttempt((n) => n + 1);
    dropLocalDraft(listId);
    onNotify?.(
      r.value.published.length
        ? t('Your site is back on the previous published order.')
        : t("Your site is back on this list's own order."),
    );
  };

  // The tab's top, brought back into view under the studio's top bar (the scroll margin) once the owner has scrolled
  // past it: a page or a list opened from the tree or the column of lists beside it, which stay in view while a list is
  // scrolled down, starts at its top, as one opened from the top of the tab does, not where the last one was scrolled
  // to; and so does each level drilled into or back out of. Measured from the top of the scroll area, not the screen:
  // on a desktop that is PageShell's slot, which starts below the top of the screen (see the list filter's
  // landingSpans).
  const bringTabIntoView = (): void => {
    const columns = columnsRef.current;
    if (!columns) return;
    const scroller = scrollParentOf(columns);
    const scrollportTop = scroller ? scroller.getBoundingClientRect().top + scroller.clientTop : 0;
    const line = scrollportTop + (parseFloat(getComputedStyle(columns).scrollMarginTop) || 0);
    if (columns.getBoundingClientRect().top < line) columns.scrollIntoView({ block: 'start' });
  };

  // A top page in the tree opens the page itself, with a row for each of its lists (and Add list where it can be
  // arranged); a page under it opens that single-list page. Picked again from one of those, the top page takes the owner
  // back. Drilled down, picking a page goes on to its lists, the open one too.
  const onOpenPage = (path: GamePage): void => {
    if (!visiblePages.some((p) => p.path === path)) return;
    setLevel('lists');
    if (path !== page) setSelectedPage(path);
    if (path !== page || drill) bringTabIntoView();
  };

  // A list's row opens the list; drilled down, it goes on to its games, the open list's too.
  const onListClick = (event: React.MouseEvent<HTMLElement>): void => {
    const id = event.currentTarget.dataset.value;
    if (!id) return;
    setPicked((prev) => ({ ...prev, [page]: id }));
    setLevel('games');
    // On a wide desktop the games scroll in their own column, which stays as the list changes: the list opened starts
    // at its top there. Drilled down, the tab's top is brought into view.
    if (!drill && id !== activeId && gamesScrollRef.current) gamesScrollRef.current.scrollTop = 0;
    if (drill) bringTabIntoView();
  };

  // Drilled down, back up a level: from a list's games to its page's lists, from those to the pages.
  const onLevelBack = (): void => {
    setLevel(level === 'games' ? 'lists' : 'pages');
    bringTabIntoView();
  };

  // The brand's own lists, read once per brand and grouped by the page each belongs to. A brand switch
  // remounts this tab, so there is nothing to clear on the way out.
  React.useEffect(() => {
    if (!ownListsOn || !casinoInstanceId || !creativesServiceConfigured()) return undefined;
    const abort = new AbortController();
    const startedAt = listWritesRef.current;
    void (async () => {
      const result = await getOwnerGameLists(casinoInstanceId, undefined, abort.signal);
      if (abort.signal.aborted) return;
      if (listWritesRef.current !== startedAt) {
        setListsReload((n) => n + 1);
        return;
      }
      setOwnListsAnswered(true);
      if (!result.ok) {
        // Saying nothing here is what makes this dangerous: the owner sees none of their chips, reads that
        // as having no lists, and builds one again, which stores a duplicate of a list they already have.
        setOwnListsFailed(result.reason !== 'unavailable');
        notifyFailure(
          result,
          t('Your own game lists could not be loaded.'),
          t('Your own game lists are not available yet.'),
        );
        return;
      }
      const byPage: Partial<Record<GamePage, CustomList[]>> = {};
      for (const list of result.value) {
        (byPage[list.page] ??= []).push({
          id: list.id,
          name: list.name,
          names: list.names ?? null,
          gameIds: list.game_ids,
          sourceId: list.source_id,
          // A service that does not say is one where every save went to the site.
          publishedIds: list.published_game_ids ?? list.game_ids,
          kind: list.kind,
          layout: list.layout,
          mode: list.mode,
          filters: list.filters,
          // A list published before lists had a mode was hand-picked; one the service says nothing about at
          // all is, as above, one whose every save went to the site.
          publishedMode: list.published_mode ?? (list.published_game_ids ? 'manual' : list.mode),
          publishedFilters: list.published_mode ? list.published_filters : list.published_game_ids ? null : list.filters,
        });
      }
      setCustomLists(byPage);
      setOwnListsLoaded(true);
      setOwnListsFailed(false);
    })();
    return () => abort.abort();
  }, [ownListsOn, casinoInstanceId, listsReload, notifyFailure, t]);

  const retryProbe = (): void => setProbeAttempt((n) => n + 1);
  // Try again beside chips under a site that could not be reached: the site is loaded again, and the open list, which
  // leaves its own Try again to this one, is read again from it (an earlier read that failed no longer stands).
  const retryReach = (): void => {
    if (activeId) {
      setUnreadable((prev) => {
        if (!prev[activeId]) return prev;
        const next = { ...prev };
        delete next[activeId];
        return next;
      });
    }
    retryProbe();
  };
  // Try again on "could not be loaded": reads again whatever failed, else loads the site's page again (it never
  // reported the lists the page shows).
  const retryLists = (): void => {
    if (sitePagesRead === 'failed') setSitePagesAttempt((n) => n + 1);
    if (arrangeable && arrangement.status === 'failed') reloadArrangement();
    if (ownListsOn && canPublish && ownListsFailed) setListsReload((n) => n + 1);
    if (!listsReadFailed) retryProbe();
  };

  // A list's name in each website language, as a create or rename stores it and as the editor's Name step shows it.
  const translateListName = React.useCallback(
    (name: string): Promise<ListNames | null> =>
      listsTenantId === undefined
        ? Promise.resolve(null)
        : localizeListName(listsTenantId, name, Object.values(providerNames)),
    [listsTenantId, providerNames],
  );

  // Add List: build one of the brand's own lists from the picked catalogue games and store it.
  //
  // The list is written to the service BEFORE the tab shows it: an optimistic chip would be indistinguishable
  // from a saved one, and a failed write would leave the owner believing they had a list they do not have.
  // An edit is stored as a draft, to publish like any other change; a new list is created with its games on
  // the site (a page cannot show a list whose source does not exist yet), and reaches the page when its
  // place is published.
  // `editingId`: the stored list this saves over (the editor's, or the open list renamed in its heading); none for a new
  // list. `inPlace`: renamed in its heading, with no editor to close, and the owner may have moved on (opened another
  // list, reordered this one) while it was being stored.
  // `edits`: the list's name in some languages, stored as given over the rest (see below): typed in its heading's
  // language chips, or as the editor's Name step left them (what it showed, the owner's own words over it).
  const saveCustomList = React.useCallback(
    async (draft: CustomListDraft, editingId: string | undefined, inPlace = false, edits?: ListNames): Promise<void> => {
      // The in-flight guard lives HERE, in the handler that writes, not only in the editor's styling: a
      // list being created carries no id, so a second save while the first is in flight stores a second list.
      // Held too while a publish runs: Publish all publishes each list as it stood at the click, and would put
      // that older copy back over this save.
      if (writingList || writing !== null) return;
      // The editor hands back the list as it stands, every stored id included (an id the catalogue cannot
      // resolve is a placeholder there, kept or removed by the owner). So it is written as given, once each.
      // Appending the unresolved ids here instead duplicated every one of them whenever the editor had
      // opened on a catalogue that failed, and the next reorder silently dropped entries.
      const nextIds = [...new Set(draft.gameIds.filter((id) => id !== ''))];
      // A hand-picked list needs a game; an automatic one needs a filter, and its ids are only its order.
      if (draft.mode === 'manual' ? nextIds.length === 0 : !draft.filters) return;
      if (!casinoInstanceId) {
        // The tab renders Add List whether or not the brand resolved, so this is reachable. Returning
        // silently left the owner clicking a button that looked live and did nothing at all.
        onNotify?.(t('This brand is still loading. Please try again in a moment.'));
        return;
      }

      // The editor this save is from (none for a rename in place): the owner may close it while the list is stored.
      const editor = listEditorRef.current;
      // The list's working order as the save starts: a reorder made while it is being stored is newer (see below).
      const draftAtStart = editingId ? drafts[editingId] : undefined;
      setWritingList(true);
      // The name in every website language, translated whenever the name changes (a new list, a rename) and stored
      // with it, the languages given over it. A translation that fails stores none (but those given), so the site shows
      // the new name rather than an older name's translations. A save that keeps the name keeps them, but for the
      // languages given (none given: none sent). Behind its flag: off, a renamed list only drops names it was given while
      // the flag was on. The save is sent even if the tab has gone meanwhile: the owner kept that name.
      const stored = editingId ? ownListById(customLists, editingId) : undefined;
      let names: ListNames | undefined;
      if (!stored || stored.name !== draft.name) {
        if (listNamesOn) {
          // Named in every language already (the editor's Name step showed them for this name): nothing to translate.
          const complete = edits !== undefined && nameLanguages.every((code) => code === 'en' || Boolean(edits[code]));
          const translated = complete ? null : await translateListName(draft.name);
          names = { ...translated, ...edits };
        } else if (stored?.names) {
          names = {};
        }
      } else if (edits) {
        names = { ...stored.names, ...edits };
      }
      // English is the name itself (EN's chip edits the name): the site's English title is the name as the owner left it.
      if (names && Object.keys(names).length > 0) names = { ...names, en: draft.name };
      const result = await saveOwnerGameList(casinoInstanceId, {
        ...(editingId ? { id: editingId } : {}),
        page,
        name: draft.name,
        ...(names ? { names } : {}),
        game_ids: nextIds,
        kind: draft.kind,
        layout: draft.layout,
        mode: draft.mode,
        filters: draft.filters,
      });
      if (!isAlive()) return;
      setWritingList(false);

      if (!result.ok) {
        notifyFailure(result, t('That list could not be saved.'), t('Saving your own game lists is not available yet.'));
        return;
      }
      const id = editingId ?? result.value.id;
      if (id === '') {
        // A create answered without the id it made. A chip with no id can never be selected, edited or
        // deleted, and the next create would replace it. The read path already drops such rows.
        onNotify?.(t('That list could not be saved.'));
        setListsReload((n) => n + 1);
        return;
      }
      // Anything this list failed to store before is settled by this write.
      setFailedOrder((prev) => (prev.startsWith(`${id}:`) ? '' : prev));
      setCustomLists((prev) => {
        // The list edited may be stored under another route of this list page's list (see `pageCustom`): the service
        // now stores it under this page, and so does the tab.
        const before = ownListById(prev, id);
        const moved = before !== undefined && !(prev[page] ?? []).some((list) => list.id === id);
        const others = moved ? withoutOwnList(prev, id) : prev;
        const lists = others[page] ?? [];
        const sourceId = before?.sourceId || result.value.source_id;
        // An edit is a draft: what the site shows stays as it was. A new list is created published.
        const saved: CustomList = {
          id,
          name: draft.name,
          names: names ? (Object.keys(names).length > 0 ? names : null) : (before?.names ?? null),
          gameIds: nextIds,
          sourceId,
          publishedIds: before?.publishedIds ?? nextIds,
          kind: draft.kind,
          layout: draft.layout,
          mode: draft.mode,
          filters: draft.filters,
          publishedMode: before?.publishedMode ?? draft.mode,
          publishedFilters: before ? before.publishedFilters : draft.filters,
        };
        return {
          ...others,
          // A new list heads the brand's lists, as it heads the page's (see `withOwnLists`). One brought here from
          // another route keeps its place on the page, which its arrangement holds.
          [page]: lists.some((list) => list.id === id)
            ? lists.map((list) => (list.id === id ? saved : list))
            : moved
              ? [...lists, saved]
              : [saved, ...lists],
        };
      });
      listWritesRef.current += 1;
      // Any leftover reorder is stale now: it is a permutation of the list BEFORE this edit, so replaying it
      // would re-add a game the owner just removed. A rename sends the games as the owner has them, so only that
      // order is settled: a reorder made while it was being stored is newer, and stays (as persistCustomOrder's).
      setDrafts((prev) => {
        if (!(id in prev) || (inPlace && !sameOrder(prev[id], nextIds))) return prev;
        // An editor's save keeps a reorder the owner made while it was being stored (its editor closed meanwhile) when
        // it orders exactly the games just stored: it is newer, and is stored next. One over other games is the stale
        // kind above.
        const saved = new Set(nextIds);
        const reorderedSince =
          !inPlace &&
          prev[id] !== draftAtStart &&
          prev[id].length === saved.size &&
          prev[id].every((gameId) => saved.has(gameId));
        if (reorderedSince) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (inPlace) return;
      // Open what was just stored: its chip becomes the active one, so it can be seen and edited. Its editor closes, but
      // not one opened since: the owner may have closed it while the list was stored (a new name is translated first,
      // for seconds) and started another.
      setPicked((prev) => ({ ...prev, [page]: id }));
      setListEditor((open) => (open === editor ? null : open));
    },
    [
      writingList,
      writing,
      page,
      casinoInstanceId,
      customLists,
      drafts,
      listNamesOn,
      nameLanguages,
      translateListName,
      notifyFailure,
      onNotify,
      t,
      isAlive,
    ],
  );
  // `names`: the list's name in each language as the editor's Name step left it.
  const handleSaveCustomList = React.useCallback(
    (draft: CustomListDraft, names?: ListNames): Promise<void> =>
      saveCustomList(draft, listEditor?.mode === 'edit' ? listEditor.id : undefined, false, names),
    [saveCustomList, listEditor],
  );
  // One of the owner's own lists renamed in its heading: stored as the editor stores it, its games as the owner has them
  // (a reorder still being stored included), the rest as it is. Its title reaches the site with the page.
  // `edits`: its name typed in some website languages from its heading's chips, stored as typed (no translation).
  const renameCustomList = (custom: CustomList, name: string, edits?: ListNames): Promise<void> =>
    saveCustomList(
      {
        name,
        gameIds: workingIdsOf(custom),
        kind: custom.kind,
        layout: custom.layout,
        mode: custom.mode,
        filters: custom.filters,
      },
      custom.id,
      true,
      edits,
    );

  // Delete drops the list and falls back to the page's first list (the chip is going away). The chip goes
  // only once the service has actually removed it, for the same reason a save waits.
  const deleteCustomList = React.useCallback(async (editingId: string): Promise<void> => {
    if (writingList || writing !== null) return;
    if (!casinoInstanceId) {
      onNotify?.(t('This brand is still loading. Please try again in a moment.'));
      return;
    }

    setWritingList(true);
    const result = await deleteOwnerGameList(casinoInstanceId, editingId);
    if (!isAlive()) return;
    setWritingList(false);

    if (!result.ok) {
      // A 404 here is not "the route is not deployed": the lists were just read through the same route
      // family. So it gets the ordinary failure copy. And a refusal may mean the list is already gone (removed
      // elsewhere), so the lists are read again: a list that no longer exists should not keep its chip.
      notifyFailure(result, t('That list could not be removed.'), t('That list could not be removed.'));
      if (result.reason !== 'failed') setListsReload((n) => n + 1);
      return;
    }
    setFailedOrder((prev) => (prev.startsWith(`${editingId}:`) ? '' : prev));
    listWritesRef.current += 1;
    const goneSource = ownListById(customLists, editingId)?.sourceId;
    if (goneSource) setDeletedSources((prev) => new Set(prev).add(goneSource));
    setCustomLists((prev) => withoutOwnList(prev, editingId));
    setPicked((prev) => ({ ...prev, [page]: undefined }));
    setListEditor(null);
    // The service takes a deleted list off every page it was published on, so what is live changed too.
    reloadArrangement();
  }, [writingList, writing, page, customLists, casinoInstanceId, notifyFailure, onNotify, t, isAlive, reloadArrangement]);
  // The editor's Delete deletes the list it edits.
  const handleDeleteCustomList = React.useCallback(async (): Promise<void> => {
    if (listEditor?.mode === 'edit') await deleteCustomList(listEditor.id);
  }, [listEditor, deleteCustomList]);
  // Drilled down on a phone, the open list is renamed from the header's title, in a popup (its name as it stands).
  const [renameAsk, setRenameAsk] = React.useState<string | null>(null);
  const [renameDraft, setRenameDraft] = React.useState('');
  React.useEffect(() => {
    if (renameAsk !== null) setRenameDraft(renameAsk);
  }, [renameAsk]);
  const renameName = renameDraft.trim();
  const confirmRename = (): void => {
    if (activeCustom && renameName !== '' && renameName !== (renameAsk ?? '').trim()) {
      void renameCustomList(activeCustom, renameName);
    }
    setRenameAsk(null);
  };
  // The kebab's Delete this list asks first, as the editor's Delete does: the list it is asked for, until answered.
  const [deleteAsk, setDeleteAsk] = React.useState<string | null>(null);
  const confirmDelete = async (): Promise<void> => {
    if (deleteAsk === null) return;
    await deleteCustomList(deleteAsk);
    setDeleteAsk(null);
  };

  /** Reorder lists' Save order: the owner's moves made on the page's draft, each as the drag it was (as the chips make
   *  it), and the popup's hides, as ONE change. Held while a publish runs: it sends the page as it stood at the click
   *  and stores that as the draft, and this change would be overwritten. The popup holds it too (`busy`), first. */
  const handleSaveListOrder = React.useCallback(
    (rows: readonly ReorderRow[], moves: readonly ListMove[]): boolean => {
      if (!workingPage || writing !== null) return false;
      setArrangementDraft(withListRows(workingPage, rows, moves));
      return true;
    },
    [workingPage, writing, setArrangementDraft],
  );
  // Opens one of the owner's lists in the editor, from the tab's Edit list or from a row of Reorder lists.
  // With its WORKING order, not the stored one: a reorder still in flight, or one whose write failed, is what
  // the owner sees, and an editor save must not quietly put the older order back.
  const editCustomList = React.useCallback(
    (custom: CustomList): void =>
      setListEditor({
        mode: 'edit',
        id: custom.id,
        initial: {
          name: custom.name,
          gameIds: drafts[custom.id] ?? custom.gameIds,
          kind: custom.kind,
          layout: custom.layout,
          mode: custom.mode,
          filters: custom.filters,
        },
      }),
    [drafts],
  );
  // Reorder lists' Edit, by the row's key: the popup's rows are its copy, whose order is not the page's.
  const handleEditList = React.useCallback(
    (key: string): void => {
      const row = pageRows.find((r) => r.key === key);
      const custom = row?.own ? customBySource.get(row.listId) : undefined;
      if (custom) editCustomList(custom);
    },
    [pageRows, customBySource, editCustomList],
  );
  // The names already on this page, so the editor's suggested name for an automatic list takes the first free
  // " 2", " 3": every list's name, the owner's and the site's, as its chip reads, except the list being edited.
  const existingNames = React.useMemo(() => {
    const editingId = listEditor?.mode === 'edit' ? listEditor.id : null;
    return orderedLists.filter((list) => list.id !== editingId).map((list) => labelOf(list.id));
  }, [orderedLists, listEditor, labelOf]);
  // A row of the page as Reorder lists names it: one of the owner's lists by its name, a site list by the
  // page's title for it, with the game count wherever the tab knows it.
  const reorderItems = React.useMemo(
    () =>
      pageRows.map((row) => {
        const custom = row.own ? customBySource.get(row.listId) : undefined;
        return {
          id: row.key,
          // Named as its chip is (on a category page, by the site's menu).
          label: custom ? custom.name : labelOf(row.listId),
          count: custom
            ? lists.find((list) => list.id === custom.id)?.count
            : siteLists.find((list) => list.id === row.listId)?.count,
          hidden: row.hidden,
          // The owner's own lists are marked and offered Edit: the site's are the platform's to change.
          own: Boolean(custom),
          mode: custom?.mode,
        };
      }),
    [pageRows, customBySource, labelOf, siteLists, lists],
  );

  // A list opened in the preview's reorder sheet becomes the open list here too, so its sheet can publish it.
  const handlePreviewListOpen = React.useCallback(
    (dsId: string | null): void => {
      const chipId = dsId ? (customBySource.get(dsId)?.id ?? dsId) : null;
      if (!chipId || !lists.some((list) => list.id === chipId)) return;
      setPicked((prev) => ({ ...prev, [page]: chipId }));
      // Drilled down, the tab behind the preview shows that list's games.
      setLevel('games');
    },
    [lists, page, customBySource],
  );

  // Undo publish for the list in a reorder sheet: the open list's, which that sheet's list becomes. A list is
  // published with the rest of the page by Publish changes; one of the owner's own has no publish to undo there.
  const publishFor = (dsId: string): GameListSheetPublish | undefined => {
    if (activeCustom && dsId === activeCustom.sourceId) {
      return { available: canPublish, canUndo: false, writing, onUndo: () => undefined };
    }
    return dsId === activeId && activePublishable
      ? {
          available: canPublish,
          canUndo: canPublish && source.canRevert,
          writing,
          onUndo: () => void handleRevert(),
        }
      : undefined;
  };

  // The open list's own actions, on the filter's row (see `ListActions`); it is published with the rest of the page
  // by Publish changes. For one of the owner's own lists, "Revert to initial state" puts its games back on the
  // published ones. For a site list, "Revert to initial state" (behind the kebab) puts the list back where it started:
  // it drops the unpublished reordering, and undoes the publish when there is one to undo.
  const handleRevertToInitial = async (): Promise<void> => {
    if (activeCustom) {
      await revertCustomList(activeCustom);
      return;
    }
    if (dirty) handleReset();
    if (activePublishable && source.canRevert) await handleRevert();
  };

  // The open list's Hide / Show in its own row, beside Edit list: the same change as its row's in Reorder lists, every
  // section that shows the list at once, and like it a draft of the page until Publish changes sends it. Offered
  // where the page can be arranged and shows the list.
  const activeRowId = activeId ? rowIdOf(activeId) : null;
  const activeOnPage = activeRowId !== null && pageRows.some((row) => row.listId === activeRowId);
  const activeHidden = activeRowId !== null && hiddenLists.has(activeRowId);
  // Saved as Reorder lists' Save order is, held by the same rule (see handleSaveListOrder).
  const toggleListHidden = (rowId: string): void => {
    const rows = pageRows.map((row) => ({
      key: row.key,
      hidden: row.listId === rowId ? !hiddenLists.has(rowId) : row.hidden,
    }));
    handleSaveListOrder(rows, []);
  };
  // A row's eye: that place of its list only (a list the page shows twice is hidden in one place, or both).
  const toggleRowHidden = (rowKey: string): void => {
    const rows = pageRows.map((row) => ({ key: row.key, hidden: row.key === rowKey ? !row.hidden : row.hidden }));
    handleSaveListOrder(rows, []);
  };
  const handleToggleActiveHidden = (): void => {
    if (activeRowId !== null) toggleListHidden(activeRowId);
  };

  const renderListActions = (compact: boolean): React.ReactNode => (
    <ListActions
      compact={compact}
      // For one of the owner's own lists, "something to go back from" is games that differ from what the site shows.
      canRevert={activeCustom ? activeGamesUnpublished : dirty || (activePublishable && source.canRevert)}
      onRevert={() => void handleRevertToInitial()}
      onPreview={() => setPreviewOpen(true)}
      onEdit={activeCustom ? () => editCustomList(activeCustom) : undefined}
      onToggleHidden={arrangeable && activeOnPage ? handleToggleActiveHidden : undefined}
      hidden={activeHidden}
      // One of the owner's own lists can be deleted from the kebab too, once it is asked (see `deleteAsk`).
      onDelete={activeCustom && ownListsOn ? () => setDeleteAsk(activeCustom.id) : undefined}
      writing={writing}
      listWriting={writingList}
    />
  );

  // While the tab loads it is drawn as it will be: on a wide desktop the page's column of lists (its search, and Add
  // list under it) beside the open list's; drilled down, the level shown.
  const renderLoading = (part: 'all' | 'lists' | 'games' = 'all'): React.ReactElement => (
    <GameListsSkeleton
      scope={part === 'all' ? 'tab' : part === 'lists' ? 'lists' : 'list'}
      addList={ownListsOn && arrangeable}
      // Drilled down the area header names the open list (headerOverridden): the level has no heading to hold room for.
      heading={part === 'games' && !headerOverridden}
      // At the page's lists it names the page: the column says only its count then (see renderListsPanel), so no room
      // is held for the page's name and the rows do not move up as they arrive.
      pageName={!headerOverridden}
    />
  );

  // `title` / `text` arrive translated (literal t() calls at each call site, so the extractor sees them).
  const renderMessage = (title: string, text: string, retry?: () => void): React.ReactElement => (
    <Wrap>
      <EmptyState>
        <EmptyTitle text={title} raw />
        <EmptyText text={text} raw />
        {retry ? (
          <RetryBtn type="button" onClick={retry}>
            <Icon name="refresh" size={14} />
            <Text tag="span" text="Try again" />
          </RetryBtn>
        ) : null}
      </EmptyState>
    </Wrap>
  );

  // The page's notes: its own lists or its arrangement could not be read, or publishing is not available yet.
  const renderPageNotes = (): React.ReactNode => (
    <>
      {status === 'failed' ? (
        <Notes role="status">
          <Note>
            <Icon name="info" size={14} />
            <NoteText text="We couldn't reach your website" />
          </Note>
          <RetryBtn type="button" onClick={retryReach}>
            <Icon name="refresh" size={14} />
            <Text tag="span" text="Try again" />
          </RetryBtn>
        </Notes>
      ) : null}

      {ownListsOn && ownListsFailed ? (
        <Notes role="status">
          <Note>
            <Icon name="info" size={14} />
            <NoteText text="Your own game lists could not be loaded." />
          </Note>
          <RetryBtn type="button" onClick={() => setListsReload((n) => n + 1)}>
            <Icon name="refresh" size={14} />
            <Text tag="span" text="Try again" />
          </RetryBtn>
        </Notes>
      ) : null}

      {/* The page's lists could not be read: without them nothing on it can be moved, hidden or placed, and a
          Reorder lists with no rows would only look like a page without lists. */}
      {arrangeable && arrangement.status === 'failed' ? (
        <Notes role="status">
          <Note>
            <Icon name="info" size={14} />
            <NoteText text="The lists on this page could not be loaded." />
          </Note>
          <RetryBtn type="button" onClick={reloadArrangement}>
            <Icon name="refresh" size={14} />
            <Text tag="span" text="Try again" />
          </RetryBtn>
        </Notes>
      ) : null}

      {canPublish ? null : (
        <Notes>
          <Note>
            <Icon name="info" size={14} />
            <NoteText text="Publishing isn't available yet. Changes are a preview only." />
          </Note>
        </Notes>
      )}
    </>
  );

  // The open list's heading over its games: its name and how many games it holds, as the column of lists heads the page.
  // One of the owner's own is renamed there, in place.
  // On a wide desktop its search and actions sit on that row, at its right, held with it over the games; drilled down
  // the heading scrolls with the page, so they stay in the list's own row, pinned as the page scrolls (see GameListGrid),
  // and are on the heading only while no games are drawn (a list that could not be loaded keeps Configure list and
  // Delete this list). Keyed by the list, so a name still being stored is not shown over the next list opened.
  const renderListHeading = (): React.ReactNode =>
    activeId ? (
      <ListHeading
        key={activeId}
        title={activeCustom ? labelOf(activeId) : t(labelOf(activeId))}
        count={lists.find((list) => list.id === activeId)?.count ?? null}
        onRename={activeCustom && ownListsOn ? (name, edits) => renameCustomList(activeCustom, name, edits) : undefined}
        names={activeCustom?.names ?? null}
        languages={nameLanguages}
        onRenameIn={
          activeCustom && ownListsOn && listNamesOn
            ? (edits) => renameCustomList(activeCustom, activeCustom.name, edits)
            : undefined
        }
        disabled={writingList || writing !== null}
        own={Boolean(activeCustom)}
        actions={drill && content ? undefined : renderListActions(compactActions)}
        searchSlot={drill ? undefined : setFilterSlot}
      />
    ) : null;

  // The open list's note: its order is not saved yet.
  const renderListNote = (): React.ReactNode => (
    <>
      {activeOrderUnsaved ? (
        <Notes role="status">
          <Note>
            <Icon name="info" size={14} />
            <NoteText text="This order is not saved yet." />
          </Note>
          {/* Clearing the failure is all a retry needs: the reorder effect writes the order again. */}
          <RetryBtn type="button" onClick={() => setFailedOrder('')}>
            <Icon name="refresh" size={14} />
            <Text tag="span" text="Try again" />
          </RetryBtn>
        </Notes>
      ) : null}
    </>
  );

  // Publish changes sends everything waiting on the page, the one publish on the tab. It sits at the
  // right of the area header's row, beside "Game Lists" (where the tab has no such header, in the column of lists,
  // under Add list). It keeps its place while away on a wide desktop, so nothing moves as it fades in and out, and folds
  // away drilled down (on a phone, in the header, where it has a row of its own), see ChangeBar.
  const headerSlot = useWebsiteHeaderAction(canPublish);
  // Publish changes names the page's lists, so it goes through their draw gate, as the rows do (the area's 2026-10-12
  // lesson): never over their skeleton, nor over the message that the site could not be reached (see renderBody).
  const listsDrawn =
    listsShown &&
    !(status === 'failed' && lists.length === 0 && !failureHeld) &&
    !(lists.length === 0 && noChipsVerdict === 'loading');
  const headerPublish = Boolean(headerSlot) && canPublish && listsDrawn;
  // On a phone, drilled down past the pages, the area header names the level (the page; the open list, with how many
  // games it holds and, for one of the owner's own, the glyph that renames it in a popup) and its back button goes up a
  // level: one way back, not two (the user, 2026-10-21). Publish changes, its icon alone, shares that row.
  // The open list is named there only once the lists are drawn, through their gate as the rows and the heading are: before
  // that the open list is not settled (and not in the site's words), and the header names the page.
  const listNamed = listsDrawn && level === 'games' && Boolean(activeId);
  const activeName = activeId ? (activeCustom ? labelOf(activeId) : t(labelOf(activeId))) : '';
  const activeCount = activeId ? lists.find((list) => list.id === activeId)?.count : undefined;
  const levelTitle = listNamed
    ? activeCount == null
      ? activeName
      : `${activeName} (${activeCount})`
    : pageLabel(page);
  const renamable = Boolean(activeCustom && ownListsOn);
  // A phone's Publish changes, opened across the header by a first press, waiting for the second (the user, 2026-10-21).
  // A press anywhere else, or nothing left to publish, closes it back to its icon.
  const [publishArmed, setPublishArmed] = React.useState(false);
  const publishBtnRef = React.useRef<HTMLButtonElement>(null);
  // Published: the button shrinks to its right into a round check ('shrink', from its width at the press, `doneFromPx`),
  // the check stays clear for a moment, then the bar fades ('fade'), then all is as it was (the user, 2026-10-21).
  const [publishDone, setPublishDone] = React.useState<'start' | 'shrink' | 'fade' | null>(null);
  const [doneFromPx, setDoneFromPx] = React.useState(40);
  const doneTimers = React.useRef<number[]>([]);
  React.useEffect(() => () => doneTimers.current.forEach((timer) => window.clearTimeout(timer)), []);
  const showPublished = (button: HTMLButtonElement): void => {
    doneTimers.current.forEach((timer) => window.clearTimeout(timer));
    setDoneFromPx(button.getBoundingClientRect().width);
    setPublishDone('start');
    setPublishArmed(false);
    // The width it had is set first, then 40px on the next frame, so the shrink is drawn. Only from 'start': in a hidden
    // tab the frames wait while the timers below run on, and a late 'shrink' would hold the button for good.
    window.requestAnimationFrame(() =>
      window.requestAnimationFrame(() => setPublishDone((done) => (done === 'start' ? 'shrink' : done))),
    );
    doneTimers.current = [
      window.setTimeout(() => setPublishDone('fade'), PUBLISH_OPEN_MS + PUBLISH_CHECK_HOLD_MS),
      window.setTimeout(() => setPublishDone(null), PUBLISH_OPEN_MS + PUBLISH_CHECK_HOLD_MS + PUBLISH_FADE_MS),
    ];
  };
  const publishShowing = publishAllShown || publishDone === 'start' || publishDone === 'shrink';
  React.useEffect(() => {
    if (!publishArmed) return undefined;
    const away = (event: PointerEvent): void => {
      if (event.target instanceof Node && publishBtnRef.current?.contains(event.target)) return;
      setPublishArmed(false);
    };
    document.addEventListener('pointerdown', away, true);
    return () => document.removeEventListener('pointerdown', away, true);
  }, [publishArmed]);
  if (publishArmed && publishDone === null && (!publishAllShown || !drill)) setPublishArmed(false);
  // Drilled down (a phone, or a desktop too narrow for the three columns), the area header names the level and the open
  // list, with the one way back up (the user, 2026-11-03: between the phone layout and the columns the tab drew its own
  // way back under the header's, and the open list's name was no title).
  const headerOverridden = useWebsiteHeaderOverride(
    drill && level !== 'pages'
      ? {
          title: levelTitle,
          onBack: onLevelBack,
          backLabel: level === 'games' ? t('Back to {{name}}', { name: pageLabel(page) }) : t('Back to the pages'),
          onTitleClick: listNamed && renamable ? () => setRenameAsk(activeName) : undefined,
          titleIcon: listNamed && renamable ? 'edit' : undefined,
          titleLabel: listNamed && renamable ? t('Rename {{name}}', { name: activeName }) : undefined,
          // One of the owner's own lists says so under its name (the user, 2026-10-21).
          tag: listNamed && activeCustom ? t('User list') : undefined,
          inlineAction: true,
          actionOpen: publishArmed,
        }
      : drill
        ? // The site's pages: the header keeps its own name and way back to the hub; Publish changes, its icon alone,
          // shares its row as on the levels below (the user, 2026-10-21).
          { inlineAction: true, actionOpen: publishArmed }
        : null,
  );
  const renderLevelBack = (): React.ReactElement => (
    <LevelBack
      type="button"
      aria-label={level === 'games' ? t('Back to {{name}}', { name: pageLabel(page) }) : t('Back to the pages')}
      onClick={onLevelBack}
    >
      <Icon name="arrow-left" size={16} />
      {level === 'games' ? <Text tag="span" text={pageLabel(page)} raw /> : <Text tag="span" text="Pages" />}
    </LevelBack>
  );
  const renderPublishAll = (inColumn: boolean, iconOnly = false): React.ReactElement => {
    const done = publishDone !== null;
    return (
      <ChangeBar
        role="status"
        $shown={publishShowing}
        $fold={drill}
        $inColumn={inColumn}
        $spans={iconOnly}
      >
        <PublishAllBtn
          ref={iconOnly ? publishBtnRef : undefined}
          type="button"
          // The FTUX's games piece anchors its publish card here: the tab's one publish since Publish this list went.
          data-ftux={PUBLISH_TARGET}
          disabled={done || shownPublishAll.current.disabled}
          tabIndex={publishShowing && !done ? undefined : -1}
          onClick={(event) => {
            if (!publishAllShown || done) return;
            // On a phone's header, a first press opens it across the row with its label; the second publishes.
            if (iconOnly && !publishArmed) {
              setPublishArmed(true);
              return;
            }
            const button = event.currentTarget;
            void handlePublishAll().then((ok) => {
              if (ok) showPublished(button);
              else setPublishArmed(false);
            });
          }}
          $iconOnly={iconOnly && !done}
          $armed={iconOnly && publishArmed && !done}
          $done={done}
          style={done ? { width: publishDone === 'start' ? doneFromPx : 40 } : undefined}
          aria-label={
            done
              ? t('Published')
              : iconOnly
                ? t('Publish changes ({{n}})', { n: shownPublishAll.current.count })
                : undefined
          }
          title={iconOnly && !done ? t('Publish changes ({{n}})', { n: shownPublishAll.current.count }) : undefined}
        >
          <Icon name={done ? 'check' : 'globe'} size={iconOnly || done ? 18 : 14} />
          <PublishLabel $shown={!done && (!iconOnly || publishArmed)} aria-hidden={done || (iconOnly && !publishArmed) || undefined}>
            <Text tag="span" text="Publish changes ({{n}})" placeholders={{ n: shownPublishAll.current.count }} />
          </PublishLabel>
        </PublishAllBtn>
      </ChangeBar>
    );
  };

  // The page's column of lists: its name and how many lists it shows, with the info point at its right (its note under
  // them), then a row for each list, the open one tinted, and Create a new list in its footer (while own lists are on,
  // on a page that can be arranged only: a list made on any other page could never take a place on it, so it would never
  // reach the site). A row has its grip where the page can be arranged (a mouse drags it from anywhere on it, a finger
  // by the grip) and an eye that hides the list on the page or shows it again. On a wide desktop the rows scroll between
  // the head and the footer, under both.
  const renderListsPanel = (): React.ReactElement => {
    const head = (
      <>
        <ListsHead>
          <ListsTitles>
            {/* On a phone the area header names the page (the user, 2026-10-21): the column says only its count. */}
            {headerOverridden ? null : <ListsTitle text={pageLabel(page)} tag="h2" raw />}
            {drawnLists.length === 1 ? (
              <ListsMeta text="1 game list" tag="span" />
            ) : (
              <ListsMeta text="{{n}} game lists" placeholders={{ n: drawnLists.length }} tag="span" />
            )}
          </ListsTitles>
          {infoPointShown ? (
            <InfoPointBtn
              type="button"
              aria-label={t('About the initial order of the games')}
              aria-expanded={infoPoint.open === true}
              aria-controls={infoPointNoteId}
              onClick={toggleInfoPoint}
            >
              <InfoPointGlyph data-glyph="info" aria-hidden="true" $shown={!noteDrawnOpen}>
                <Icon name="info" size={16} />
              </InfoPointGlyph>
              <InfoPointGlyph data-glyph="close" aria-hidden="true" $shown={noteDrawnOpen}>
                <Icon name="close" size={14} />
              </InfoPointGlyph>
            </InfoPointBtn>
          ) : null}
        </ListsHead>
        {infoPointShown ? (
          <InfoPointNote id={infoPointNoteId} $open={noteDrawnOpen}>
            <InfoPointFold>
              <InfoPointBox>
                <InfoPointText
                  tag="div"
                  text="For the initial order of the games, we took inspiration from top-performing sites in the Mexican region, including: {{sites}}."
                  placeholders={{ sites: INSPIRATION_SITES.join(', ') }}
                />
              </InfoPointBox>
            </InfoPointFold>
          </InfoPointNote>
        ) : null}
        {canPublish && !headerSlot ? renderPublishAll(true) : null}
      </>
    );
    return (
      <ListsColumn $pane={!drill}>
        {drill ? head : null}
        <ListRowsScroll $pane={!drill}>
          {/* On a wide desktop the rows scroll up to the top of the frame, under the head, which stays put and blurs
              them (as the open list's heading does). */}
          {drill ? null : <ListsTop>{head}</ListsTop>}
          <ListRows ref={chipRows.listRef} role="group" aria-label={t('Game lists on this page')}>
            {columnRows.map((entry, index) => {
              const { list } = entry;
              const rowKey = entry.row?.key;
              const { shift, animated, dragged } = chipRows.row(index);
              const transform =
                shift !== 0 || dragged
                  ? `translateY(${shift}px)${dragged ? ` scale(${SORTABLE_ANIM.LIFT_SCALE})` : ''}`
                  : undefined;
              const sortable = chipDraggable(entry);
              return (
                <RowWrap
                  key={entry.key}
                  $dragged={dragged}
                  $animated={animated}
                  style={transform ? { transform } : undefined}
                  {...(sortable ? chipRows.rowProps(index) : {})}
                >
                  <ListRow
                    label={labelOf(list.id)}
                    count={list.count}
                    value={list.id}
                    appearance={entry.appearance}
                    selected={entry.key === openRow}
                    // The owner's own lists are marked as theirs, a list the page hides says so (the row reads "Hidden"
                    // to a screen reader too), and the site's lists carry no mark.
                    own={customById.has(list.id)}
                    hidden={entry.row?.hidden ?? false}
                    unpublished={hasUnpublished(list.id)}
                    grip={sortable ? chipRows.handleProps(index) : undefined}
                    onToggleHidden={
                      canToggleHidden(entry) && rowKey !== undefined ? () => toggleRowHidden(rowKey) : undefined
                    }
                    onClick={(event) => {
                      setPickedRow(entry.key);
                      onListClick(event);
                    }}
                  />
                  {hasUnpublished(list.id) ? (
                    <ScreenReaderText>{t('This list has unpublished changes.')}</ScreenReaderText>
                  ) : null}
                </RowWrap>
              );
            })}
          </ListRows>
          {/* Create a new list, in the column's footer: always in view, the rows scrolling under it. */}
          {arrangeable && ownListsOn ? <ListsFooterSurface aria-hidden="true" /> : null}
          {arrangeable && ownListsOn ? (
            <ListsFooter $pane={!drill}>
              <AboveAppChrome when={isMobile} />
              {/* The FTUX's own-list piece lights it; full width inside its mark, as the footer stretches it. */}
              <FtuxTarget id="games-add-list" fill>
                <AddListBtn
                  variant="outline"
                  icon="plus"
                  label="Create a new list"
                  fullWidth
                  onClick={() => {
                    ftuxSignal('games-list-editor-opened');
                    setListEditor({ mode: 'new' });
                  }}
                />
              </FtuxTarget>
            </ListsFooter>
          ) : null}
        </ListRowsScroll>
      </ListsColumn>
    );
  };

  // The open list: its games (a list the site does not draw has none to show here, and says why), or why they cannot
  // be shown, or their placeholders while they load.
  const renderOpenList = (): React.ReactNode => (
    <>
      {!content && activeId !== undefined && awayIds.has(activeId) ? (
        // A list the site does not draw has no games to show here: hidden, Show brings it back; shown again (a draft),
        // it reaches the site once published.
        <EmptyState>
          {hiddenLists.has(activeId) ? <EmptyTitle text="Hidden on your site" /> : null}
          <EmptyText text="Your site shows these changes once you publish them. A hidden list keeps its place, so showing it again puts it back there." />
        </EmptyState>
      ) : content ? (
        <GameListGrid
          key={activeId}
          // On a wide desktop the games scroll inside their own column, as a sheet's do: the filter row pins at its top.
          placement={drill ? 'page' : 'dialog'}
          games={games}
          providerNames={providerNames}
          onReorder={handleReorder}
          // On a wide desktop the list's search and its actions are on its heading's row (see renderListHeading).
          filterPortal={drill ? null : filterSlot}
          filterActions={drill ? renderListActions : undefined}
          // On a phone the filter is folded away to its magnifier, the actions beside it (the user, 2026-10-21).
          collapsibleFilter={isMobile}
          onDraggingChange={handleDraggingChange}
          // FTUX - Getting Started's games piece: the open list plays its micro drag while the piece shows how.
          demoLoop={dragDemoOn}
          // The FTUX's games piece lights the open list's games wherever the tab draws them.
          listTarget
          unavailable={activeOwn?.unavailable}
          onRemove={removable ? handleRemoveGame : undefined}
          // One of the owner's lists is drawn from the slim catalogue, which carries no art: each game shown reads its
          // own by id. A site list's games come from the site with theirs.
          artById={activeCustom !== undefined}
        />
      ) : (activeId && (unreadable[activeId] || (status === 'failed' && !activeCustom))) ||
        (activeCustom && catalogFailed) ? (
        // A site that could not be reached sends no games: the list says so, not that it is loading.
        <EmptyState>
          <EmptyTitle text="We couldn't load this list" />
          {activeCustom ? (
            <EmptyText text="The game catalogue could not be loaded, so this list's games cannot be shown. Please try again." />
          ) : (
            <EmptyText text="Your website did not send the games in this list. Please try again." />
          )}
          {/* A site that could not be reached is tried again from the page's note, which says so. */}
          {status === 'failed' && !activeCustom ? null : (
            <RetryBtn
              type="button"
              onClick={() => {
                if (activeCustom) setCatalogAttempt((n) => n + 1);
                if (!activeId) return;
                setUnreadable((prev) => {
                  const next = { ...prev };
                  delete next[activeId];
                  return next;
                });
                // A list the site never reported: its page is loaded again, which may report it this time.
                if (activeUnreported) retryProbe();
              }}
            >
              <Icon name="refresh" size={14} />
              <Text tag="span" text="Try again" />
            </RetryBtn>
          )}
          {/* A list the catalogue cannot draw still has Configure list and Delete this list on its heading's row,
              over this (drilled down too). */}
        </EmptyState>
      ) : (
        <GameListsSkeleton scope="list" />
      )}
    </>
  );

  // The open page: on a wide desktop its column of lists beside the open list, with the notes over it (`all`); drilled
  // down, its lists with the page's notes (`lists`), or the open list with its note (`games`).
  const renderBody = (part: 'all' | 'lists' | 'games' = 'all'): React.ReactElement => {
    if (!siteUrl) {
      return resolving
        ? renderLoading(part)
        : renderMessage(
            t('Your website is not available yet'),
            t('Game lists are read from your live website. They will show here once your site is online.'),
          );
    }
    // Chips drawn on the service's word once the frame has failed (see `siteAnswered`) are kept: that failure is said
    // beside them (below), never by taking them back to this message. With no chip to keep (a page come back to, read again from
    // a load of the site that failed), it is this message, never the loading state the verdict gives an unconnected site.
    if (status === 'failed' && (!listsShown || lists.length === 0) && !failureHeld) {
      return renderMessage(
        t("We couldn't reach your website"),
        t('Game lists are read from your live website. Check that it is online, then try again.'),
        retryProbe,
      );
    }
    if (!listsShown) return renderLoading(part);
    if (lists.length === 0) {
      if (noChipsVerdict === 'empty') {
        return renderMessage(
          t('No game lists on this page'),
          t('This page of your website does not show any game lists.'),
        );
      }
      if (noChipsVerdict === 'failed') {
        return renderMessage(
          t('The lists on this page could not be loaded.'),
          t('Game lists are read from your live website. Check that it is online, then try again.'),
          retryLists,
        );
      }
      return renderLoading(part);
    }

    // Past every check above, the lists are drawn.
    if (part === 'lists') {
      return (
        <Body>
          {renderPageNotes()}
          {renderListsPanel()}
        </Body>
      );
    }
    if (part === 'games') {
      // The page's notes too: a site that could not be reached says so here, with its Try again (the open list's own
      // message leaves it to them).
      return (
        <Body $tight={headerOverridden}>
          {headerOverridden ? (
            // The header names the list; where no games are drawn its actions (Configure list, Delete this list) stay.
            !content && activeId ? (
              <LevelActions>{renderListActions(true)}</LevelActions>
            ) : null
          ) : (
            renderListHeading()
          )}
          {renderPageNotes()}
          {/* With no area header to hold it (the website-hub flag off), Publish changes is on this level too: the FTUX's
              games piece stands its card by it here, and the owner publishes what they just moved. */}
          {canPublish && !headerSlot ? renderPublishAll(true) : null}
          {renderListNote()}
          {renderOpenList()}
        </Body>
      );
    }
    return (
      <Body $split>
        {renderListsPanel()}
        <GamesPane>
          {/* Held still under the FTUX's hold, as the page is: the games piece's zone is the list in it. */}
          <GamesScroll ref={gamesScrollRef} data-ftux-hold-still="">
            <GamesHead ref={setGamesHead} data-list-heading="">
              {renderListHeading()}
            </GamesHead>
            {renderPageNotes()}
            {renderListNote()}
            {renderOpenList()}
          </GamesScroll>
        </GamesPane>
      </Body>
    );
  };

  return (
    <>
      {frameSrc ? (
        <ProbeFrame
          key={`${frameSrc}#${probeAttempt}`}
          ref={probeRef}
          src={frameSrc}
          onLoad={onFrameLoad}
          title={t('Website data')}
          aria-hidden="true"
          tabIndex={-1}
          sandbox="allow-scripts allow-same-origin"
        />
      ) : null}

      {/* On a wide desktop the site's pages as a tree in a column, the open page beside it. Drilled down, one level at a
          time: the pages, then the open page's lists, then the open list's games, each with the way back up. */}
      <Columns ref={columnsRef} $tree={!drill}>
        {!drill ? (
          <>
            <TreeColumn>
              <PageTree nodes={treeNodes} openPath={page} onOpen={onOpenPage} loading={!pageTabsSettled} />
            </TreeColumn>
            <MainColumn>
              <SwitchFade key={page} $free>
                {renderBody()}
              </SwitchFade>
            </MainColumn>
          </>
        ) : level === 'pages' ? (
          <PagesLevel>
            <PageTree nodes={treeNodes} openPath={page} onOpen={onOpenPage} loading={!pageTabsSettled} />
          </PagesLevel>
        ) : (
          <MainColumn>
            {headerOverridden ? null : renderLevelBack()}
            <SwitchFade key={`${page}#${level}`} $free>
              {renderBody(level)}
            </SwitchFade>
          </MainColumn>
        )}
      </Columns>

      {headerSlot && headerPublish ? createPortal(renderPublishAll(false, headerOverridden), headerSlot) : null}

      {previewOpen && activeId ? (
        <PreviewOverlay
          title="Preview"
          center={<DevicePreviewPill value={device} onChange={setDevice} />}
          onClose={() => {
            setPreviewOpen(false);
            setDevice('desktop');
          }}
        >
          <GameListPreview
            // No site (its loader) while the frame waits for the catalogue the owner's lists are drawn from.
            siteUrl={previewHeld ? '' : siteUrl}
            page={page}
            // The site's name for the list: an own list's source id, not its own id.
            dsId={rowIdOf(activeId)}
            drafts={previewAllDrafts}
            onDraftChange={handlePreviewDraftChange}
            contents={previewContents}
            onContent={handleContent}
            providerNames={providerNames}
            listLabel={labelOf}
            onListOpen={handlePreviewListOpen}
            publishFor={publishFor}
            device={device}
          />
        </PreviewOverlay>
      ) : null}

      {manageOpen ? (
        <ListReorderModal
          pageLabel={pageLabel(page)}
          items={reorderItems}
          // A row's Edit opens the editor OVER this sheet, which stays open beneath it with its copy: a list deleted
          // there drops out of the copy, the rest of it kept. Nothing reaches the page until Save order.
          onEdit={handleEditList}
          onSave={handleSaveListOrder}
          busy={writing !== null}
          listWriting={writingList}
          onClose={() => setManageOpen(false)}
        />
      ) : null}

      {/* On a phone, the open list's name in the header renames it here (the user, 2026-10-21). */}
      <Modal
        open={renameAsk !== null}
        title={t('Rename {{name}}', { name: renameAsk ?? '' })}
        onClose={() => setRenameAsk(null)}
        footer={
          <>
            <Button variant="text" label={t('Cancel')} onClick={() => setRenameAsk(null)} />
            <Button
              label={t('Save')}
              disabled={renameName === '' || renameName === (renameAsk ?? '').trim() || writingList || writing !== null}
              onClick={confirmRename}
            />
          </>
        }
      >
        {/* As the list editor caps it. */}
        <Field label={t('List name')} value={renameDraft} onChange={setRenameDraft} maxLength={60} />
      </Modal>

      {/* The kebab's Delete this list, asked as the editor's Delete asks. */}
      <Modal
        open={deleteAsk !== null}
        title={t('Delete this list?')}
        onClose={() => setDeleteAsk(null)}
        footer={
          <>
            <Button variant="text" label={t('Keep it')} onClick={() => setDeleteAsk(null)} />
            <DeleteListBtn
              type="button"
              disabled={writingList}
              aria-busy={writingList}
              onClick={writingList ? undefined : () => void confirmDelete()}
            >
              {writingList ? <Text tag="span" text="Deleting…" /> : <Text tag="span" text="Delete" />}
            </DeleteListBtn>
          </>
        }
      >
        <Text tag="paragraph" text={deleteAsk !== null ? labelOf(deleteAsk) : ''} raw />
      </Modal>

      {listEditor && ownListsOn ? (
        <CustomListEditor
          mode={listEditor.mode}
          initial={listEditor.mode === 'edit' ? listEditor.initial : undefined}
          providerNames={providerNames}
          existingNames={existingNames}
          busy={writingList || writing !== null}
          // A category page draws every list as a carousel, so it offers no layout to choose.
          layoutChoice={currentPage.kind !== 'category'}
          loadMarketCollections={loadMarketCollections}
          languages={nameLanguages}
          names={listEditor.mode === 'edit' ? (ownListById(customLists, listEditor.id)?.names ?? null) : null}
          translate={listNamesOn ? translateListName : undefined}
          onSave={handleSaveCustomList}
          onDelete={listEditor.mode === 'edit' ? handleDeleteCustomList : undefined}
          onClose={() => setListEditor(null)}
        />
      ) : null}
    </>
  );
};

export default GameLists;
